import { useCallback, useEffect, useState, useRef } from 'react';
import { commercialOfferService, planService } from '../services/api';
import StripeProductPriceSelector from '../components/StripeProductPriceSelector';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const EMPTY_PRICE_OVERRIDE = {
  recurringPriceMode: 'INHERIT',
  activationPriceMode: 'INHERIT',
  recurringStripePriceIds: [],
  recurringStripePrices: [],
  activationStripePriceId: '',
};

function StripeOfferPricePicker({ type, selectedIds, initialPrices = [], onChange, disabled = false }) {
  return (
    <StripeProductPriceSelector
      mode="prices"
      priceType={type}
      selectedPriceIds={selectedIds}
      initialPrices={initialPrices}
      onPriceChange={onChange}
      disabled={disabled}
      singleProduct={type === 'recurring'}
      singleProductErrorMessage={type === 'recurring'
        ? 'Per il ruolo SUBSCRIPTION (canone ricorrente), tutti i Price devono appartenere allo stesso Product Stripe. Il Price del ruolo ACTIVATION (una tantum) può appartenere a un Product diverso.'
        : ''}
    />
  );
}

const getDefaultShareMessage = (offerLabel = '') => {
  const offerLine = offerLabel ? ` per l'offerta "${offerLabel}"` : '';
  return `C'è una nuova offerta commerciale SoonTip${offerLine} riservata per te.\n\nClicca sul pulsante qui sotto per attivare l'offerta e completare la registrazione in pochi minuti.`;
};

const parseEmailRecipients = (value = '') => (
  Array.from(
    new Set(
      value
        .split(/[\n,;]+/)
        .map(email => email.trim().toLowerCase())
        .filter(Boolean)
    )
  )
);

const getEmptyAccessSearchState = () => ({ query: '', results: [], loading: false, showDropdown: false });

const normalizeAccessEmail = (email = '') => email.trim().toLowerCase();

const isSameAccessRule = (rule = {}, entry = {}) => {
  const normalizedEntryEmail = entry.email ? normalizeAccessEmail(entry.email) : '';
  const normalizedRuleEmail = rule.email ? normalizeAccessEmail(rule.email) : '';

  return Boolean(
    (entry.userId && rule.userId && rule.userId === entry.userId) ||
    (normalizedEntryEmail && normalizedRuleEmail && normalizedRuleEmail === normalizedEntryEmail)
  );
};

const hasExistingAccessRule = (source, entry = {}) => {
  const rules = Array.isArray(source) ? source : source?.accessRules || [];
  return rules.some(rule => isSameAccessRule(rule, entry));
};

const buildPlanVersionOptions = (plans = []) => {
  return (plans || [])
    .flatMap((plan) => (plan.versions || [])
      .filter((version) => version.status === 'PUBLISHED' && version.activeForNewSubscriptions)
      .map((version) => ({
        value: version.id,
        label: `${plan.name} · v${version.version}`,
      })))
    .sort((a, b) => a.label.localeCompare(b.label, 'it'));
};

const OVERRIDE_HIDDEN_KEYS = new Set([
  'SETUP_FEE_CENTS',
  'ACTIVATION_FEE_CENTS',
  'SOFTWARE_SUBSCRIPTION_FEE_CENTS',
]);

const normalizeOverrideRows = (planVersion, existingOverrides = [], featureDefinitions = []) => {
  const overrideMap = new Map((existingOverrides || []).map((item) => [
    item.featureDefinitionId || item.featureDefinition?.id,
    item,
  ]));

  const planFeatureRows = (planVersion?.features || [])
    .filter((item) => item?.featureDefinition?.id && !OVERRIDE_HIDDEN_KEYS.has(item.featureDefinition.key))
    .map((item) => ({
      featureDefinitionId: item.featureDefinition.id,
      key: item.featureDefinition.key,
      name: item.featureDefinition.name || item.featureDefinition.key,
      valueType: item.featureDefinition.valueType || 'JSON',
      baseValue: item.value,
    }));

  const planIds = new Set(planFeatureRows.map((row) => row.featureDefinitionId));
  const catalogRows = (featureDefinitions || [])
    .filter((fd) => fd?.id && fd?.key && !OVERRIDE_HIDDEN_KEYS.has(fd.key) && !planIds.has(fd.id))
    .map((fd) => ({
      featureDefinitionId: fd.id,
      key: fd.key,
      name: fd.name || fd.key,
      valueType: fd.valueType || 'JSON',
      baseValue: null,
    }));

  const rows = [...planFeatureRows, ...catalogRows].map((row) => {
    const existing = overrideMap.get(row.featureDefinitionId);
    const overrideValue = existing ? existing.value : row.baseValue;

    return {
      ...row,
      overrideEnabled: Boolean(existing),
      overrideValue,
      jsonText: row.valueType === 'JSON'
        ? JSON.stringify(overrideValue ?? {}, null, 2)
        : '',
    };
  }).sort((first, second) => first.name.localeCompare(second.name, 'it'));

  return rows;
};

const renderBooleanStatus = (value, compact = false) => {
  const isEnabled = Boolean(value);
  const label = isEnabled ? 'Abilitato' : 'Disabilitato';
  const palette = isEnabled
    ? { bg: '#f0fdf4', border: '#86efac', text: '#166534', dot: '#16a34a' }
    : { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', dot: '#dc2626' };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: compact ? '2px 8px' : '3px 9px',
      borderRadius: 999,
      fontSize: compact ? 11 : 12,
      fontWeight: 700,
      color: palette.text,
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: compact ? '7px' : '8px', height: compact ? '7px' : '8px', borderRadius: '50%', background: palette.dot }} />
      {label}
    </span>
  );
};

const BooleanToggle = ({ value, onChange, disabled = false }) => {
  const isEnabled = Boolean(value);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!isEnabled)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        border: isEnabled ? '1px solid #86efac' : '1px solid #fca5a5',
        background: isEnabled ? '#f0fdf4' : '#fef2f2',
        color: isEnabled ? '#166534' : '#991b1b',
        borderRadius: 999,
        padding: '8px 12px',
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        gap: 8,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: isEnabled ? '#16a34a' : '#dc2626', boxShadow: isEnabled ? '0 0 0 4px rgba(22, 163, 74, 0.15)' : '0 0 0 4px rgba(220, 38, 38, 0.15)' }} />
        {isEnabled ? 'Abilitato' : 'Disabilitato'}
      </span>
      <span style={{ width: '34px', height: '18px', borderRadius: 999, background: isEnabled ? '#16a34a' : '#d1d5db', padding: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: isEnabled ? 'flex-end' : 'flex-start' }}>
        <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </span>
    </button>
  );
};

const formatFeatureBaseValue = (row) => {
  if (row.valueType === 'BOOLEAN') return row.baseValue ? 'Abilitato' : 'Disabilitato';
  if ((row.valueType === 'INTEGER' || row.valueType === 'FLOAT') && (row.baseValue == null || Number(row.baseValue) === 0)) {
    return 'Illimitato';
  }
  if (row.key === 'SOONTIP_FEE' && row.valueType === 'JSON') {
    const value = row.baseValue && typeof row.baseValue === 'object' ? row.baseValue : { feePercent: 20, feeFixedCents: 0 };
    return `${value.feePercent ?? 0}% + ${((value.feeFixedCents || 0) / 100).toFixed(2).replace('.', ',')}€`;
  }
  if (row.valueType === 'JSON') {
    try {
      return JSON.stringify(row.baseValue ?? {}, null, 0);
    } catch {
      return '{}';
    }
  }
  return String(row.baseValue ?? '');
};

const toFeatureOverridesPayload = (rows = []) => {
  return rows
    .filter((row) => row.overrideEnabled)
    .map((row) => {
      let value;
      if (row.valueType === 'BOOLEAN') {
        value = Boolean(row.overrideValue);
      } else if (row.valueType === 'INTEGER') {
        value = row.overrideValue === null || row.overrideValue === undefined || row.overrideValue === ''
          ? null
          : parseInt(row.overrideValue, 10);
      } else if (row.valueType === 'FLOAT') {
        value = row.overrideValue === null || row.overrideValue === undefined || row.overrideValue === ''
          ? null
          : parseFloat(row.overrideValue);
      } else if (row.valueType === 'JSON') {
        if (row.key === 'SOONTIP_FEE') {
          const parsed = typeof row.overrideValue === 'object' && row.overrideValue !== null
            ? row.overrideValue
            : JSON.parse(row.jsonText || '{}');
          value = {
            feePercent: parseInt(parsed?.feePercent ?? 0, 10),
            feeFixedCents: parseInt(parsed?.feeFixedCents ?? 0, 10),
          };
        } else {
          try {
            value = JSON.parse(row.jsonText || '{}');
          } catch (error) {
            throw new Error(`JSON non valido per override "${row.name}"`);
          }
        }
      } else {
        value = row.overrideValue;
      }

      return {
        featureDefinitionId: row.featureDefinitionId === 'SOONTIP_FEE_MANUAL' ? null : row.featureDefinitionId,
        key: row.key === 'SOONTIP_FEE' ? 'SOONTIP_FEE' : undefined,
        value,
      };
    });
};

const AccessRulesEditor = ({
  title,
  rules = [],
  searchState,
  setSearchState,
  onSearchChange,
  onAddEntry,
  onRemoveRule,
  emptyText,
  helperText,
}) => {
  const filteredResults = (searchState?.results || []).filter(
    user => !hasExistingAccessRule(rules, { userId: user.id, email: user.email })
  );
  const query = searchState?.query || '';
  const canAddTypedEmail = EMAIL_REGEX.test(query)
    && !filteredResults.some(r => normalizeAccessEmail(r.email || '') === normalizeAccessEmail(query))
    && !hasExistingAccessRule(rules, { email: query });

  return (
    <div style={{ marginBottom: 0 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>{title}</h4>
      {helperText && <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px' }}>{helperText}</p>}

      {rules.length === 0 ? (
        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>{emptyText}</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', maxHeight: '120px', overflowY: 'auto', padding: '4px 2px' }}>
          {rules.map(rule => (
            <div key={rule.id || rule.userId || rule.email} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px 4px 5px', background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '20px', fontSize: '13px' }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#C21F7A', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, flexShrink: 0 }}>
                {(rule.label || rule.email || '?').charAt(0).toUpperCase()}
              </span>
              <div>
                <span style={{ fontWeight: 600, color: '#1f1135' }}>
                  {rule.label || rule.email || rule.userId?.substring(0, 16) + '…'}
                </span>
                {rule.label && (rule.email || rule.userId) && (
                  <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '4px' }}>
                    {rule.email || rule.userId?.substring(0, 12) + '…'}
                  </span>
                )}
              </div>
              <button onClick={() => onRemoveRule(rule)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', padding: '0', lineHeight: 1, fontSize: '15px', flexShrink: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={query}
          onChange={e => onSearchChange(e.target.value)}
          onBlur={() => setTimeout(() => setSearchState(prev => ({ ...prev, showDropdown: false })), 150)}
          onFocus={() => query.trim() && setSearchState(prev => ({ ...prev, showDropdown: true }))}
          placeholder="Cerca per nome, cognome o email..."
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
        />
        {searchState?.loading && (
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#9ca3af' }}>ricerca...</span>
        )}
        {searchState?.showDropdown && query.trim().length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 50, maxHeight: '200px', overflowY: 'auto', marginTop: '2px' }}>
            {filteredResults.map(user => (
              <div
                key={user.id}
                onMouseDown={() => onAddEntry({ userId: user.id, email: user.email, label: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined })}
                style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <div>
                  <span style={{ fontWeight: 500, fontSize: '13px' }}>{user.first_name} {user.last_name}</span>
                  <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '6px' }}>{user.email}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#C21F7A' }}>+ Aggiungi</span>
              </div>
            ))}
            {canAddTypedEmail && (
              <div
                onMouseDown={() => onAddEntry({ email: query })}
                style={{ padding: '8px 12px', cursor: 'pointer', borderTop: filteredResults.length > 0 ? '1px solid #e5e7eb' : undefined }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <span style={{ color: '#C21F7A', fontSize: '13px' }}>+ Aggiungi "{query}" (email non registrata)</span>
              </div>
            )}
            {filteredResults.length === 0 && !canAddTypedEmail && query.trim().length >= 2 && (
              <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6b7280' }}>
                {hasExistingAccessRule(rules, { email: query })
                  ? 'Questo utente/email è già presente tra le regole di accesso.'
                  : 'Nessun nuovo risultato disponibile da aggiungere.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CommercialOffers = () => {
  const [offers, setOffers] = useState([]);
  const [legacyOffers, setLegacyOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [detailTab, setDetailTab] = useState('new-system');
  const [detailLoading, setDetailLoading] = useState(false);
  const [invitationModal, setInvitationModal] = useState({ open: false, offerId: null });
  const [viewLinkModal, setViewLinkModal] = useState({ open: false, inv: null });
  const [accessSearch, setAccessSearch] = useState(getEmptyAccessSearchState());
  const [createAccessSearch, setCreateAccessSearch] = useState(getEmptyAccessSearchState());
  const accessDebounceRef = useRef(null);
  const createAccessDebounceRef = useRef(null);
  const tableScrollRef = useRef(null);

  // Sort + filter state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Create form state
  const [formData, setFormData] = useState({
    label: '',
    basePlanVersionId: '',
    notes: '',
    expiresAt: '',
    feeOverridePercent: '',
    feeOverrideFixedCents: '',
    trialDaysOverride: '',
    automaticPromotionCodeId: '',
    ...EMPTY_PRICE_OVERRIDE,
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formCouponNotice, setFormCouponNotice] = useState('');
  const [createAccessRules, setCreateAccessRules] = useState([]);
  const [createOverrideRows, setCreateOverrideRows] = useState([]);

  // Edit modal state
  const [editModal, setEditModal] = useState({ open: false, offer: null });
  const [editForm, setEditForm] = useState({ label: '', basePlanVersionId: '', notes: '', expiresAt: '', feeOverridePercent: '', feeOverrideFixedCents: '', trialDaysOverride: '', automaticPromotionCodeId: '', ...EMPTY_PRICE_OVERRIDE });
  const [editOverrideRows, setEditOverrideRows] = useState([]);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editCouponNotice, setEditCouponNotice] = useState('');
  const [editWarning, setEditWarning] = useState(null);

  // Invitation form state
  const [invitationForm, setInvitationForm] = useState({ label: '', maxUses: '', ttlDays: '30', noExpiry: false });
  const [invitationResult, setInvitationResult] = useState(null);
  const [invitationSubmitting, setInvitationSubmitting] = useState(false);
  const [shareEmailModal, setShareEmailModal] = useState({
    open: false,
    offerId: null,
    offerLabel: '',
    offerExpiresAt: null,
    invitation: null,
    emails: '',
    message: '',
    sending: false,
    success: '',
    error: ''
  });
  const [contractPreview, setContractPreview] = useState({
    open: false,
    loading: false,
    title: '',
    version: '',
    html: '',
    note: '',
    error: '',
    offerLabel: ''
  });

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, offerId: null, checkData: null, loading: false, acting: false });
  const [planVersionOptions, setPlanVersionOptions] = useState([]);
  const [plansCatalog, setPlansCatalog] = useState([]);
  const [featureDefinitionsCatalog, setFeatureDefinitionsCatalog] = useState([]);
  const [stripePromotionCodes, setStripePromotionCodes] = useState([]);

  // Signed contracts modal state
  const [signedContractsModal, setSignedContractsModal] = useState({ open: false, offerId: null, contracts: [], loading: false, error: '' });

  const loadOffers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [offersData, plansData, featureDefinitionsData, promotionCodesData] = await Promise.all([
        commercialOfferService.list(),
        planService.list(),
        planService.listFeatureDefinitions(),
        commercialOfferService.listStripePromotionCodes(),
      ]);
      setOffers(offersData.data || []);
      setPlansCatalog(plansData.data || []);
      setFeatureDefinitionsCatalog(featureDefinitionsData.data || []);
      setStripePromotionCodes(promotionCodesData.data || []);

      try {
        const legacyData = await commercialOfferService.listLegacy();
        setLegacyOffers(legacyData.data || []);
      } catch (legacyErr) {
        console.warn('Failed to load legacy commercial offers:', legacyErr);
        setLegacyOffers([]);
      }
      const options = buildPlanVersionOptions(plansData.data || []);
      const allPlanVersions = (plansData.data || []).flatMap(plan => plan.versions || []);
      const selectedBasePlanVersionId = options[0]?.value || '';
      setPlanVersionOptions(options);
      setFormData((prev) => {
        if (prev.basePlanVersionId === selectedBasePlanVersionId) return prev;
        return { ...prev, basePlanVersionId: selectedBasePlanVersionId };
      });
      const selectedPlanVersion = allPlanVersions.find(version => version.id === selectedBasePlanVersionId);
      if (selectedPlanVersion) {
        setCreateOverrideRows(normalizeOverrideRows(selectedPlanVersion, [], featureDefinitionsData.data || []));
      }
    } catch (err) {
      console.error('Failed to load commercial offers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const loadOfferDetail = async (id, source = 'new') => {
    try {
      setDetailLoading(true);
      if (source === 'legacy') {
        const data = await commercialOfferService.getLegacy(id);
        setSelectedOffer({ ...(data.data || {}), __source: 'legacy' });
        setDetailTab('legacy-readonly');
      } else {
        const data = await commercialOfferService.get(id);
        setSelectedOffer({ ...(data.data || {}), __source: 'new' });
        setDetailTab('new-system');
      }
    } catch (err) {
      alert('Errore nel caricamento dell\'offerta: ' + err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const openSignedContractsModal = async (offerId, source = 'new') => {
    // Load offer details and select it in the table
    await loadOfferDetail(offerId, source);
    
    // Load and open signed contracts modal
    setSignedContractsModal({ open: true, offerId, contracts: [], loading: true, error: '' });
    try {
      const data = await commercialOfferService.getSignedContracts(offerId);
      setSignedContractsModal(prev => ({ ...prev, contracts: data.data || [], loading: false }));
    } catch (err) {
      console.error('Failed to load signed contracts:', err);
      setSignedContractsModal(prev => ({ ...prev, loading: false, error: err.message || 'Errore caricamento contratti firmati.' }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'REGISTERED': { color: '#6c757d', label: 'Registrato' },
      'EMAIL_VERIFIED': { color: '#17a2b8', label: 'Email Verificata' },
      'ONBOARDING_STARTED': { color: '#ffc107', label: 'Onboarding' },
      'CONSENTS_ACCEPTED': { color: '#fd7e14', label: 'Consensi OK' },
      'CONTRACT_SIGNED_PENDING_PAYMENT':     { color: '#d97706', label: 'In Attesa Pagamento' },
      'CONTRACT_SIGNED_PENDING_ACTIVATION': { color: '#e83e8c', label: 'In Attesa Attivazione' },
      'ACTIVE': { color: '#28a745', label: 'Attivo' },
      'SUSPENDED': { color: '#dc3545', label: 'Sospeso' },
      'CLOSED': { color: '#6c757d', label: 'Chiuso' }
    };

    const config = statusConfig[status] || { color: '#6c757d', label: status || 'UNKNOWN' };
    return config;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.basePlanVersionId) {
      setFormError('Seleziona un piano base pubblicato prima di creare l\'offerta.');
      return;
    }
    if (!getPromotionCodeCompatibility(formData.automaticPromotionCodeId, formData.basePlanVersionId, formData).compatible) {
      setFormError('Il coupon selezionato non è compatibile con i prodotti Stripe del piano base.');
      return;
    }
    setFormSubmitting(true);
    try {
      const featureOverrides = toFeatureOverridesPayload(createOverrideRows);
      await commercialOfferService.create({
        label: formData.label,
        basePlanVersionId: formData.basePlanVersionId,
        notes: formData.notes || null,
        expiresAt: formData.expiresAt || null,
        feeOverridePercent:    formData.feeOverridePercent    !== '' ? parseInt(formData.feeOverridePercent, 10)    : null,
        feeOverrideFixedCents: formData.feeOverrideFixedCents !== '' ? parseInt(formData.feeOverrideFixedCents, 10) : null,
        trialDaysOverride:     formData.trialDaysOverride     !== '' ? parseInt(formData.trialDaysOverride, 10)     : null,
        automaticPromotionCodeId: formData.automaticPromotionCodeId || null,
        recurringPriceMode: formData.recurringPriceMode,
        activationPriceMode: formData.activationPriceMode,
        recurringStripePriceIds: formData.recurringStripePriceIds,
        activationStripePriceId: formData.activationStripePriceId || null,
        featureOverrides,
        accessRules: createAccessRules.map(rule => ({
          userId: rule.userId || undefined,
          email: rule.email || undefined,
          label: rule.label || undefined,
        }))
      });
      setShowCreateForm(false);
      setFormData({
        label: '',
        basePlanVersionId: planVersionOptions[0]?.value || '',
        notes: '',
        expiresAt: '',
        feeOverridePercent: '',
        feeOverrideFixedCents: '',
        trialDaysOverride: '',
        automaticPromotionCodeId: '',
        ...EMPTY_PRICE_OVERRIDE,
      });
      setCreateAccessRules([]);
      setCreateOverrideRows([]);
      setCreateAccessSearch(getEmptyAccessSearchState());
      loadOffers();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

const handleReactivate = async (id) => {
    if (!window.confirm('Riattivare questa offerta?')) return;
    try {
      await commercialOfferService.reactivate(id);
      loadOffers();
      if (selectedOffer?.id === id) loadOfferDetail(id);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const openDeleteModal = async (id) => {
    setDeleteModal({ open: true, offerId: id, checkData: null, loading: true, acting: false });
    try {
      const data = await commercialOfferService.checkDeletion(id);
      setDeleteModal(prev => ({ ...prev, checkData: data, loading: false }));
    } catch (err) {
      setDeleteModal({ open: false, offerId: null, checkData: null, loading: false, acting: false });
      alert('Errore nel caricamento: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSoftDelete = async () => {
    const { offerId } = deleteModal;
    setDeleteModal(prev => ({ ...prev, acting: true }));
    try {
      await commercialOfferService.deactivate(offerId);
      setDeleteModal({ open: false, offerId: null, checkData: null, loading: false, acting: false });
      loadOffers();
      if (selectedOffer?.id === offerId) setSelectedOffer(null);
    } catch (err) {
      setDeleteModal(prev => ({ ...prev, acting: false }));
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleHardDelete = async () => {
    const { offerId } = deleteModal;
    setDeleteModal(prev => ({ ...prev, acting: true }));
    try {
      await commercialOfferService.hardDelete(offerId);
      setDeleteModal({ open: false, offerId: null, checkData: null, loading: false, acting: false });
      loadOffers();
      if (selectedOffer?.id === offerId) setSelectedOffer(null);
    } catch (err) {
      setDeleteModal(prev => ({ ...prev, acting: false }));
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const openEditModal = (offer) => {
    // Format expiresAt as datetime-local string (YYYY-MM-DDTHH:mm)
    const expiresAtValue = offer.expiresAt
      ? new Date(offer.expiresAt).toISOString().slice(0, 16)
      : '';
    setEditForm({
      label: offer.label,
      basePlanVersionId: offer.basePlanVersionId || '',
      notes: offer.notes || '',
      expiresAt: expiresAtValue,
      feeOverridePercent:    offer.feeOverridePercent    != null ? String(offer.feeOverridePercent)    : '',
      feeOverrideFixedCents: offer.feeOverrideFixedCents != null ? String(offer.feeOverrideFixedCents) : '',
      trialDaysOverride:     offer.trialDaysOverride     != null ? String(offer.trialDaysOverride)     : '',
      automaticPromotionCodeId: offer.automaticPromotionCodeId || '',
      recurringPriceMode: offer.recurringPriceMode || 'INHERIT',
      activationPriceMode: offer.activationPriceMode || 'INHERIT',
      recurringStripePriceIds: (offer.priceOverrides || [])
        .filter((price) => price.purpose === 'SUBSCRIPTION')
        .map((price) => price.stripePriceId),
      recurringStripePrices: (offer.priceOverrides || [])
        .filter((price) => price.purpose === 'SUBSCRIPTION'),
      activationStripePriceId: (offer.priceOverrides || [])
        .find((price) => price.purpose === 'ACTIVATION')?.stripePriceId || '',
    });

    const allVersions = plansCatalog.flatMap(plan => plan.versions || []);
    const selectedPlanVersion = allVersions.find(v => v.id === offer.basePlanVersionId);
    setEditOverrideRows(normalizeOverrideRows(selectedPlanVersion, offer.featureOverrides || [], featureDefinitionsCatalog));

    setEditError('');
    setEditWarning(null);
    setEditModal({ open: true, offer });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditWarning(null);
    if (!getPromotionCodeCompatibility(editForm.automaticPromotionCodeId, editForm.basePlanVersionId, editForm).compatible) {
      setEditError('Il coupon selezionato non è compatibile con i prodotti Stripe del piano base.');
      return;
    }
    setEditSubmitting(true);
    try {
      const featureOverrides = toFeatureOverridesPayload(editOverrideRows);
      const result = await commercialOfferService.update(editModal.offer.id, {
        label: editForm.label,
        basePlanVersionId: editForm.basePlanVersionId,
        notes: editForm.notes || null,
        expiresAt: editForm.expiresAt || null,
        feeOverridePercent:    editForm.feeOverridePercent    !== '' ? parseInt(editForm.feeOverridePercent, 10)    : null,
        feeOverrideFixedCents: editForm.feeOverrideFixedCents !== '' ? parseInt(editForm.feeOverrideFixedCents, 10) : null,
        trialDaysOverride:     editForm.trialDaysOverride     !== '' ? parseInt(editForm.trialDaysOverride, 10)     : null,
        automaticPromotionCodeId: editForm.automaticPromotionCodeId || null,
        recurringPriceMode: editForm.recurringPriceMode,
        activationPriceMode: editForm.activationPriceMode,
        recurringStripePriceIds: editForm.recurringStripePriceIds,
        activationStripePriceId: editForm.activationStripePriceId || null,
        featureOverrides,
      });
      loadOffers();
      if (selectedOffer?.id === editModal.offer.id) loadOfferDetail(editModal.offer.id);
      if (result?.warning?.code === 'AWAITING_SIGNATURE_VOIDED') {
        setEditWarning(result.warning);
      } else {
        setEditModal({ open: false, offer: null });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status === 409) {
        setEditError('Impossibile modificare le fee: ci sono contratti già firmati con questa offerta.');
      } else {
        setEditError(msg);
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setInvitationSubmitting(true);
    setInvitationResult(null);
    try {
      const data = await commercialOfferService.createInvitation(invitationModal.offerId, {
        label: invitationForm.label || undefined,
        maxUses: invitationForm.maxUses ? parseInt(invitationForm.maxUses) : undefined,
        ttlDays: invitationForm.noExpiry ? 0 : parseInt(invitationForm.ttlDays) || 0,
      });
      setInvitationResult(data.data);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    } finally {
      setInvitationSubmitting(false);
    }
  };

  const openShareEmailModal = (offerId, invitation, offerLabel, offerExpiresAt = null) => {
    setShareEmailModal({
      open: true,
      offerId,
      offerLabel: offerLabel || invitation?.offerLabel || '',
      offerExpiresAt: offerExpiresAt || invitation?.offerExpiresAt || null,
      invitation,
      emails: '',
      message: getDefaultShareMessage(offerLabel || invitation?.offerLabel || invitation?.label || ''),
      sending: false,
      success: '',
      error: ''
    });
  };

  const closeShareEmailModal = () => {
    setShareEmailModal({
      open: false,
      offerId: null,
      offerLabel: '',
      offerExpiresAt: null,
      invitation: null,
      emails: '',
      message: '',
      sending: false,
      success: '',
      error: ''
    });
  };

  const handleSendInvitationEmail = async (e) => {
    e.preventDefault();

    const recipients = parseEmailRecipients(shareEmailModal.emails);
    if (recipients.length === 0) {
      setShareEmailModal(prev => ({ ...prev, error: 'Inserisci almeno un indirizzo email.', success: '' }));
      return;
    }

    const invalidRecipients = recipients.filter(email => !EMAIL_REGEX.test(email));
    if (invalidRecipients.length > 0) {
      setShareEmailModal(prev => ({
        ...prev,
        error: `Email non valide: ${invalidRecipients.join(', ')}`,
        success: ''
      }));
      return;
    }

    setShareEmailModal(prev => ({ ...prev, sending: true, error: '', success: '' }));

    try {
      const response = await commercialOfferService.sendInvitationEmail(
        shareEmailModal.offerId,
        shareEmailModal.invitation.id,
        {
          emails: recipients,
          message: shareEmailModal.message.trim() || getDefaultShareMessage(shareEmailModal.offerLabel || shareEmailModal.invitation?.label || '')
        }
      );

      const sentCount = response?.data?.sent?.length ?? recipients.length;
      const failedCount = response?.data?.failed?.length ?? 0;

      setShareEmailModal(prev => ({
        ...prev,
        sending: false,
        success: failedCount > 0
          ? `Email inviata a ${sentCount} destinatari, con ${failedCount} invii non riusciti.`
          : `Email inviata con successo a ${sentCount} destinatari.`
      }));
    } catch (err) {
      setShareEmailModal(prev => ({
        ...prev,
        sending: false,
        error: err.response?.data?.error || err.message || 'Invio email non riuscito.'
      }));
    }
  };

  const handleDeleteInvitation = async (offerId, invitationId) => {
    if (!window.confirm('Revocare questo link di invito?')) return;
    try {
      await commercialOfferService.deleteInvitation(offerId, invitationId);
      loadOfferDetail(offerId);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const openContractPreview = async (offer) => {
    if (!offer?.id) return;

    setContractPreview({
      open: true,
      loading: true,
      title: '',
      version: '',
      html: '',
      note: '',
      error: '',
      offerLabel: offer.label || ''
    });

    try {
      const response = offer.__source === 'legacy'
        ? await commercialOfferService.getLegacyContractPreview(offer.id)
        : await commercialOfferService.getContractPreview(offer.id);
      setContractPreview({
        open: true,
        loading: false,
        title: response?.data?.title || 'Anteprima contratto SoonTip',
        version: response?.data?.version || '',
        html: response?.data?.html || '',
        note: response?.data?.note || '',
        error: '',
        offerLabel: offer.label || ''
      });
    } catch (err) {
      setContractPreview(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || err.message || 'Impossibile caricare l’anteprima del contratto.'
      }));
    }
  };

  const openCreateContractPreview = async () => {
    const draftLabel = formData.label?.trim() || 'Nuova offerta (bozza)';

    setContractPreview({
      open: true,
      loading: true,
      title: '',
      version: '',
      html: '',
      note: '',
      error: '',
      offerLabel: draftLabel
    });

    try {
      const featureOverrides = toFeatureOverridesPayload(createOverrideRows);
      const response = await commercialOfferService.previewContractDraft({
        label: draftLabel,
        basePlanVersionId: formData.basePlanVersionId,
        featureOverrides,
        feeOverridePercent:    formData.feeOverridePercent    !== '' ? parseInt(formData.feeOverridePercent, 10)    : null,
        feeOverrideFixedCents: formData.feeOverrideFixedCents !== '' ? parseInt(formData.feeOverrideFixedCents, 10) : null,
        trialDaysOverride:     formData.trialDaysOverride     !== '' ? parseInt(formData.trialDaysOverride, 10)     : null,
        automaticPromotionCodeId: formData.automaticPromotionCodeId || null,
        recurringPriceMode: formData.recurringPriceMode,
        activationPriceMode: formData.activationPriceMode,
        recurringStripePriceIds: formData.recurringStripePriceIds,
        activationStripePriceId: formData.activationStripePriceId || null,
        setupFeeCents: formData.setupFeeCents === '' ? 0 : parseInt(formData.setupFeeCents, 10),
        softwareSubscriptionFeeCents: formData.softwareSubscriptionFeeCents === '' ? 0 : parseInt(formData.softwareSubscriptionFeeCents, 10),
        notes: formData.notes || '',
        expiresAt: formData.expiresAt || null
      });

      setContractPreview({
        open: true,
        loading: false,
        title: response?.data?.title || 'Anteprima contratto SoonTip',
        version: response?.data?.version || '',
        html: response?.data?.html || '',
        note: response?.data?.note || '',
        error: '',
        offerLabel: draftLabel
      });
    } catch (err) {
      setContractPreview(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || err.message || 'Impossibile caricare l’anteprima del contratto.'
      }));
    }
  };

  const closeContractPreview = () => {
    setContractPreview({
      open: false,
      loading: false,
      title: '',
      version: '',
      html: '',
      note: '',
      error: '',
      offerLabel: ''
    });
  };

  const handleAccessSearchInput = (value, setSearchState, debounceRef) => {
    setSearchState(prev => ({ ...prev, query: value, showDropdown: value.length > 0 }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setSearchState(prev => ({ ...prev, results: [], loading: false }));
      return;
    }
    setSearchState(prev => ({ ...prev, loading: true }));
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await commercialOfferService.searchUsers(value.trim());
        setSearchState(prev => ({ ...prev, results: data.data || [], loading: false, showDropdown: true }));
      } catch {
        setSearchState(prev => ({ ...prev, results: [], loading: false }));
      }
    }, 300);
  };

  const handleAccessSearchChange = (value) => handleAccessSearchInput(value, setAccessSearch, accessDebounceRef);
  const handleCreateAccessSearchChange = (value) => handleAccessSearchInput(value, setCreateAccessSearch, createAccessDebounceRef);

  const handleAddAccessEntry = async (entry) => {
    if (!selectedOffer) return;

    if (hasExistingAccessRule(selectedOffer, entry)) {
      alert('Questa regola di accesso è già presente.');
      setAccessSearch(p => ({ ...p, showDropdown: false }));
      return;
    }

    try {
      await commercialOfferService.addAccess(selectedOffer.id, entry);
      setAccessSearch(getEmptyAccessSearchState());
      loadOfferDetail(selectedOffer.id);
      loadOffers();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddDraftAccessEntry = (entry) => {
    if (hasExistingAccessRule(createAccessRules, entry)) {
      alert('Questa regola di accesso è già presente.');
      setCreateAccessSearch(prev => ({ ...prev, showDropdown: false }));
      return;
    }

    setCreateAccessRules(prev => ([
      ...prev,
      {
        id: entry.userId ? `draft-user-${entry.userId}` : `draft-email-${normalizeAccessEmail(entry.email || '')}`,
        userId: entry.userId || null,
        email: entry.email ? normalizeAccessEmail(entry.email) : null,
        label: entry.label || null,
      }
    ]));
    setCreateAccessSearch(getEmptyAccessSearchState());
  };

  const handleRemoveDraftAccessRule = (entry) => {
    setCreateAccessRules(prev => prev.filter(rule => !isSameAccessRule(rule, entry)));
  };

  const handleRemoveAccess = async (offerId, accessId) => {
    if (!window.confirm('Rimuovere questa regola di accesso?')) return;
    try {
      await commercialOfferService.removeAccess(offerId, accessId);
      loadOfferDetail(offerId);
      loadOffers();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatCents = (cents) => `€${(cents / 100).toFixed(2)}`;

  const getPlanPriceSummary = (planVersion) => {
    const prices = Array.isArray(planVersion?.prices)
      ? planVersion.prices.filter((price) => !price?.archivedAt)
      : [];

    return {
      recurring: prices.filter((price) => price?.priceType === 'RECURRING'),
      oneTime: prices.filter((price) => price?.priceType === 'ONE_TIME'),
    };
  };

  const renderPlanPriceSummary = (planVersion) => {
    if (!planVersion) return null;

    const { recurring, oneTime } = getPlanPriceSummary(planVersion);
    const formatInterval = (price) => {
      if (price?.priceType !== 'RECURRING') return '';
      const interval = price?.interval || 'month';
      const count = Number(price?.intervalCount || 1);
      const labels = {
        month: count === 1 ? 'mese' : 'mesi',
        year: count === 1 ? 'anno' : 'anni',
        week: count === 1 ? 'settimana' : 'settimane',
        day: count === 1 ? 'giorno' : 'giorni',
      };
      return count === 1 ? `/${labels[interval] || interval}` : `/ogni ${count} ${labels[interval] || interval}`;
    };

    const recurringIntervalPriority = {
      day: 1,
      week: 2,
      month: 3,
      year: 4,
    };

    const sortedRecurring = [...recurring].sort((a, b) => {
      const aPriority = recurringIntervalPriority[a?.interval] ?? 99;
      const bPriority = recurringIntervalPriority[b?.interval] ?? 99;
      if (aPriority !== bPriority) return aPriority - bPriority;

      const aIntervalCount = Number(a?.intervalCount || 1);
      const bIntervalCount = Number(b?.intervalCount || 1);
      if (aIntervalCount !== bIntervalCount) return aIntervalCount - bIntervalCount;

      return Number(a?.amountCents || 0) - Number(b?.amountCents || 0);
    });

    return (
      <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
          Costi del piano selezionato
        </div>
        {recurring.length > 0 ? (
          <div style={{ marginBottom: recurring.length > 0 && oneTime.length > 0 ? '6px' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', overflowX: 'auto', fontSize: '12px', color: '#0f172a' }}>
              <span style={{ whiteSpace: 'nowrap' }}><strong>Ricorrente:</strong></span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {sortedRecurring
                  .map((price) => `${formatCents(price.amountCents)}${formatInterval(price)}`)
                  .join(' • ')}
              </span>
            </div>
          </div>
        ) : null}
        {oneTime.length > 0 ? (
          <div style={{ display: 'grid', gap: '4px' }}>
            {oneTime.map((price) => (
              <div key={`onetime-${price.id}`} style={{ fontSize: '12px', color: '#0f172a' }}>
                <strong>Una tantum:</strong> {formatCents(price.amountCents)}
              </div>
            ))}
          </div>
        ) : null}
        {recurring.length === 0 && oneTime.length === 0 && (
          <div style={{ fontSize: '12px', color: '#64748b' }}>Nessun costo ricorrente o una tantum associato a questo piano.</div>
        )}
      </div>
    );
  };

  const buildSimulatedDiffRows = (offer) => {
    const allVersions = plansCatalog.flatMap(p => p.versions || []);
    const pv = allVersions.find(v => v.id === offer?.basePlanVersionId);

    const activeBasePrices = (pv?.prices || []).filter((price) => !price.archivedAt);
    const baseRecurringPrices = activeBasePrices.filter(
      (price) => price.purpose === 'SUBSCRIPTION' && price.priceType === 'RECURRING'
    );
    const baseActivationPrice = activeBasePrices.find(
      (price) => price.purpose === 'ACTIVATION' && price.priceType === 'ONE_TIME'
    ) || null;
    const customRecurringPrices = (offer?.priceOverrides || []).filter(
      (price) => price.purpose === 'SUBSCRIPTION'
    );
    const customActivationPrice = (offer?.priceOverrides || []).find(
      (price) => price.purpose === 'ACTIVATION'
    ) || null;
    const recurringPriceMode = offer?.recurringPriceMode || 'INHERIT';
    const activationPriceMode = offer?.activationPriceMode || 'INHERIT';

    const recurringKey = (price) => `${price?.interval || 'month'}:${Number(price?.intervalCount || 1)}`;
    const formatPrice = (price) => {
      if (!price) return 'Non previsto';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: String(price.currency || 'EUR').toUpperCase(),
      }).format(Number(price.amountCents || 0) / 100);
    };
    const formatRecurringLabel = (key) => {
      const [interval, rawCount] = key.split(':');
      const count = Number(rawCount || 1);
      const singularLabels = {
        day: 'giornaliero',
        week: 'settimanale',
        month: 'mensile',
        year: 'annuale',
      };
      const pluralLabels = {
        day: 'giorni',
        week: 'settimane',
        month: 'mesi',
        year: 'anni',
      };
      return count === 1
        ? singularLabels[interval] || interval
        : `ogni ${count} ${pluralLabels[interval] || interval}`;
    };
    const recurringPriority = { day: 1, week: 2, month: 3, year: 4 };
    const baseRecurringByKey = new Map(baseRecurringPrices.map((price) => [recurringKey(price), price]));
    const customRecurringByKey = new Map(customRecurringPrices.map((price) => [recurringKey(price), price]));
    const recurringKeys = Array.from(new Set([
      ...baseRecurringByKey.keys(),
      ...(recurringPriceMode === 'CUSTOM' ? customRecurringByKey.keys() : []),
    ])).sort((left, right) => {
      const [leftInterval, leftCount] = left.split(':');
      const [rightInterval, rightCount] = right.split(':');
      return (recurringPriority[leftInterval] ?? 99) - (recurringPriority[rightInterval] ?? 99)
        || Number(leftCount) - Number(rightCount);
    });
    const recurringPriceRows = (recurringKeys.length > 0 ? recurringKeys : ['month:1']).map((key) => {
      const basePrice = baseRecurringByKey.get(key) || null;
      const effectivePrice = recurringPriceMode === 'CUSTOM'
        ? customRecurringByKey.get(key) || null
        : basePrice;
      return {
        label: `PREZZO RICORRENTE (${formatRecurringLabel(key)})`,
        baseValue: formatPrice(basePrice),
        overrideValue: formatPrice(effectivePrice),
        changed: recurringPriceMode === 'CUSTOM',
      };
    });
    const effectiveActivationPrice = activationPriceMode === 'NONE'
      ? null
      : activationPriceMode === 'CUSTOM'
        ? customActivationPrice
        : baseActivationPrice;
    const activationPriceRow = {
      label: 'PREZZO ATTIVAZIONE (una tantum)',
      baseValue: formatPrice(baseActivationPrice),
      overrideValue: activationPriceMode === 'NONE' ? 'Nessun costo' : formatPrice(effectiveActivationPrice),
      changed: activationPriceMode !== 'INHERIT',
    };

    const baseFeePercent = pv?.feePercent ?? 20;
    const baseFeeFixedCents = pv?.feeFixedCents ?? 0;
    const targetFeePercent = offer?.feeOverridePercent != null ? Number(offer.feeOverridePercent) : baseFeePercent;
    const targetFeeFixedCents = offer?.feeOverrideFixedCents != null ? Number(offer.feeOverrideFixedCents) : baseFeeFixedCents;
    const baseTrialDays = pv?.trialDays ?? 0;
    const targetTrialDays = offer?.trialDaysOverride != null ? Number(offer.trialDaysOverride) : baseTrialDays;

    // Build override map from offer.featureOverrides
    const overrideMap = new Map((offer?.featureOverrides || []).map(o => [o.featureDefinitionId || o.featureDefinition?.id, o.value]));

    // Build feature rows from the plan version's actual features
    const coveredIds = new Set();
    const featureRows = (pv?.features || [])
      .filter(f => f?.featureDefinition?.id && !OVERRIDE_HIDDEN_KEYS.has(f.featureDefinition.key))
      .map(f => {
        const fd = f.featureDefinition;
        coveredIds.add(fd.id);
        const baseVal = f.value;
        const hasOverride = overrideMap.has(fd.id);
        const overrideVal = hasOverride ? overrideMap.get(fd.id) : baseVal;
        const formatVal = (val) => {
          if (fd.valueType === 'BOOLEAN') return val ? 'Abilitato' : 'Disabilitato';
          if (fd.valueType === 'INTEGER' || fd.valueType === 'FLOAT') return val == null || Number(val) === 0 ? 'Illimitato' : String(val);
          if (fd.valueType === 'JSON') return JSON.stringify(val ?? {});
          return String(val ?? '');
        };
        return {
          label: fd.key,
          baseValue: formatVal(baseVal),
          overrideValue: formatVal(overrideVal),
          changed: hasOverride && JSON.stringify(overrideVal) !== JSON.stringify(baseVal),
        };
      });

    // Also add rows for overridden features not present in pv.features (look up from catalog)
    const extraRows = (offer?.featureOverrides || [])
      .filter(o => {
        const id = o.featureDefinitionId || o.featureDefinition?.id;
        return id && !coveredIds.has(id);
      })
      .map(o => {
        const fd = o.featureDefinition || featureDefinitionsCatalog.find(d => d.id === (o.featureDefinitionId || o.featureDefinition?.id));
        if (!fd || OVERRIDE_HIDDEN_KEYS.has(fd.key)) return null;
        const overrideVal = o.value;
        const formatVal = (val) => {
          if (fd.valueType === 'BOOLEAN') return val ? 'Abilitato' : 'Disabilitato';
          if (fd.valueType === 'INTEGER' || fd.valueType === 'FLOAT') return val == null || Number(val) === 0 ? 'Illimitato' : String(val);
          if (fd.valueType === 'JSON') return JSON.stringify(val ?? {});
          return String(val ?? '');
        };
        return {
          label: fd.key,
          baseValue: '(non definito nel piano)',
          overrideValue: formatVal(overrideVal),
          changed: true,
        };
      })
      .filter(Boolean);

    return [
      ...recurringPriceRows,
      activationPriceRow,
      {
        label: 'SOONTIP_FEE (%)',
        baseValue: `${baseFeePercent}%`,
        overrideValue: `${targetFeePercent}%`,
        changed: targetFeePercent !== baseFeePercent,
      },
      {
        label: 'SOONTIP_FEE (fisso)',
        baseValue: formatCents(baseFeeFixedCents),
        overrideValue: formatCents(targetFeeFixedCents),
        changed: targetFeeFixedCents !== baseFeeFixedCents,
      },
      {
        label: 'TRIAL (giorni)',
        baseValue: `${baseTrialDays}`,
        overrideValue: `${targetTrialDays}`,
        changed: targetTrialDays !== baseTrialDays,
      },
      ...featureRows,
      ...extraRows,
    ];
  };

  const handleCreateBasePlanChange = (basePlanVersionId) => {
    setFormData((previous) => {
      if (previous.automaticPromotionCodeId) {
        setFormCouponNotice('Coupon automatico rimosso: riselezionalo per verificarne la compatibilità con il nuovo piano.');
      } else {
        setFormCouponNotice('');
      }
      return { ...previous, basePlanVersionId, automaticPromotionCodeId: '' };
    });
    const allVersions = plansCatalog.flatMap(plan => plan.versions || []);
    const selectedPlanVersion = allVersions.find(v => v.id === basePlanVersionId);
    setCreateOverrideRows(normalizeOverrideRows(selectedPlanVersion, [], featureDefinitionsCatalog));
  };

  const toggleCreateForm = () => {
    if (!showCreateForm) {
      const basePlanVersionId = formData.basePlanVersionId || planVersionOptions[0]?.value || '';
      const selectedPlanVersion = plansCatalog
        .flatMap(plan => plan.versions || [])
        .find(version => version.id === basePlanVersionId);
      setCreateOverrideRows(normalizeOverrideRows(selectedPlanVersion, [], featureDefinitionsCatalog));
    }
    setShowCreateForm(previous => !previous);
  };

  const handleEditBasePlanChange = (basePlanVersionId) => {
    setEditForm((previous) => {
      if (previous.automaticPromotionCodeId) {
        setEditCouponNotice('Coupon automatico rimosso: riselezionalo per verificarne la compatibilità con il nuovo piano.');
      } else {
        setEditCouponNotice('');
      }
      return { ...previous, basePlanVersionId, automaticPromotionCodeId: '' };
    });
    const allVersions = plansCatalog.flatMap(plan => plan.versions || []);
    const selectedPlanVersion = allVersions.find(v => v.id === basePlanVersionId);
    setEditOverrideRows(normalizeOverrideRows(selectedPlanVersion, [], featureDefinitionsCatalog));
  };

  const renderFeeOverrideCard = (overridePercent, overrideFixedCents, onChange, basePlanVersionId, disabled = false) => {
    const allVersions = plansCatalog.flatMap(p => p.versions || []);
    const pv = allVersions.find(v => v.id === basePlanVersionId);
    const basePct = pv?.feePercent ?? 20;
    const baseFixed = pv?.feeFixedCents ?? 0;
    const enabled = overridePercent !== '';
    const pct = enabled ? Number(overridePercent) : basePct;
    const fixed = enabled ? Number(overrideFixedCents || 0) : baseFixed;
    const toggle = () => {
      if (enabled) { onChange('feeOverridePercent', ''); onChange('feeOverrideFixedCents', ''); }
      else { onChange('feeOverridePercent', String(basePct)); onChange('feeOverrideFixedCents', String(baseFixed)); }
    };
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{
          border: enabled ? '1px solid #f9a8d4' : '1px solid #e5e7eb',
          borderRadius: 12, padding: 12,
          background: enabled ? 'linear-gradient(180deg, #fff7fb 0%, #ffffff 40%)' : '#ffffff',
          boxShadow: enabled ? '0 6px 20px rgba(194,31,122,0.10)' : '0 1px 4px rgba(15,23,42,0.04)',
          transition: 'all .18s ease',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 14, marginBottom: 2 }}>Fee SoonTip</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 10.5, color: '#334155', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 7px' }}>FEE_OVERRIDE</span>
                <span style={{ fontSize: 10.5, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 7px' }}>%+¢</span>
              </div>
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={toggle}
              style={{
                border: enabled ? '1px solid #c21f7a' : '1px solid #cbd5e1',
                background: enabled ? '#c21f7a' : '#ffffff',
                color: enabled ? '#ffffff' : '#334155',
                borderRadius: 999, padding: '5px 10px', fontSize: 12, fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {enabled ? 'Override attivo' : 'Attiva override'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: enabled ? 10 : 0 }}>
            Base: <span style={{ color: '#334155', fontWeight: 700 }}>{basePct}% + EUR {(baseFixed / 100).toFixed(2).replace('.', ',')}</span>
          </div>
          {enabled && (
            <div style={{ border: '1px dashed #f3b3d8', borderRadius: 10, background: '#fff8fc', padding: 10 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Percentuale (%)</label>
                <input
                  type="number" min="0" max="100" disabled={disabled}
                  value={pct}
                  onChange={e => onChange('feeOverridePercent', e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#ffffff' }}
                />
                <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Importo fisso (centesimi €)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="number" min="0" step="1" disabled={disabled}
                    value={fixed}
                    onChange={e => onChange('feeOverrideFixedCents', e.target.value === '' ? '0' : String(parseInt(e.target.value, 10)))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#ffffff' }}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>= EUR {(fixed / 100).toFixed(2).replace('.', ',')}</span>
                </div>
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, color: '#166534', fontWeight: 700, textAlign: 'center' }}>
                  Fee override: {pct}% + {(fixed / 100).toFixed(2).replace('.', ',')}€
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPriceOverrideCard = (form, setForm, disabled = false) => (
    <div style={{ marginBottom: 14, border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, background: '#f8fbff' }}>
      <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>Prezzi dell&apos;offerta</div>
      <div style={{ marginTop: 3, marginBottom: 12, color: '#64748b', fontSize: 12 }}>
        I prezzi personalizzati vengono selezionati dal catalogo Stripe e verificati dal backend. Nessun importo viene inserito manualmente.
      </div>

      <label style={{ display: 'block', marginBottom: 5, fontWeight: 700, fontSize: 12 }}>Canone ricorrente</label>
      <select
        value={form.recurringPriceMode}
        disabled={disabled}
        onChange={(event) => setForm((previous) => ({
          ...previous,
          recurringPriceMode: event.target.value,
          recurringStripePriceIds: event.target.value === 'CUSTOM' ? previous.recurringStripePriceIds : [],
          recurringStripePrices: event.target.value === 'CUSTOM' ? previous.recurringStripePrices : [],
        }))}
        style={{ width: '100%', padding: 9, border: '1px solid #cbd5e1', borderRadius: 7, background: '#fff' }}
      >
        <option value="INHERIT">Prezzi del piano base</option>
        <option value="CUSTOM">Prezzi personalizzati da Stripe</option>
      </select>
      {form.recurringPriceMode === 'CUSTOM' && (
        <StripeOfferPricePicker
          type="recurring"
          selectedIds={form.recurringStripePriceIds}
          initialPrices={form.recurringStripePrices}
          disabled={disabled}
          onChange={(ids) => setForm((previous) => ({ ...previous, recurringStripePriceIds: ids }))}
        />
      )}

      <label style={{ display: 'block', margin: '14px 0 5px', fontWeight: 700, fontSize: 12 }}>Costo di attivazione</label>
      <select
        value={form.activationPriceMode}
        disabled={disabled}
        onChange={(event) => setForm((previous) => ({
          ...previous,
          activationPriceMode: event.target.value,
          activationStripePriceId: event.target.value === 'CUSTOM' ? previous.activationStripePriceId : '',
        }))}
        style={{ width: '100%', padding: 9, border: '1px solid #cbd5e1', borderRadius: 7, background: '#fff' }}
      >
        <option value="INHERIT">Come piano base</option>
        <option value="NONE">Nessun costo di attivazione</option>
        <option value="CUSTOM">Prezzo personalizzato da Stripe</option>
      </select>
      {form.activationPriceMode === 'CUSTOM' && (
        <StripeOfferPricePicker
          type="one_time"
          selectedIds={form.activationStripePriceId ? [form.activationStripePriceId] : []}
          disabled={disabled}
          onChange={(ids) => setForm((previous) => ({ ...previous, activationStripePriceId: ids[0] || '' }))}
        />
      )}
    </div>
  );

  const renderTrialOverrideCard = (overrideDays, onChange, basePlanVersionId, disabled = false) => {
    const allVersions = plansCatalog.flatMap(p => p.versions || []);
    const planVersion = allVersions.find(v => v.id === basePlanVersionId);
    const baseDays = planVersion?.trialDays ?? 0;
    const enabled = overrideDays !== '';
    const effectiveDays = enabled ? Number(overrideDays || 0) : baseDays;

    return (
      <div style={{ marginBottom: 14, border: enabled ? '1px solid #93c5fd' : '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: enabled ? '#eff6ff' : '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>Periodo di prova</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              Base piano: <strong>{baseDays > 0 ? `${baseDays} giorni` : 'nessuna prova'}</strong>
            </div>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange('trialDaysOverride', enabled ? '' : String(baseDays))}
            style={{ border: enabled ? '1px solid #2563eb' : '1px solid #cbd5e1', background: enabled ? '#2563eb' : '#ffffff', color: enabled ? '#ffffff' : '#334155', borderRadius: 999, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {enabled ? 'Override attivo' : 'Attiva override'}
          </button>
        </div>
        {enabled && (
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 5 }}>Giorni di prova sul canone ricorrente</label>
            <input
              type="number"
              min="0"
              max="365"
              step="1"
              disabled={disabled}
              value={effectiveDays}
              onChange={e => onChange('trialDaysOverride', e.target.value === '' ? '0' : e.target.value)}
              style={{ width: 100, padding: 9, border: '1px solid #d1d5db', borderRadius: 8 }}
            />
            <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b' }}>0 disabilita esplicitamente la prova per questa offerta</span>
          </div>
        )}
      </div>
    );
  };

  const getPromotionCodeCompatibility = (selectedId, planVersionId, pricingForm = null) => {
    const promotionCode = stripePromotionCodes.find((item) => item.id === selectedId);
    if (!promotionCode) return { compatible: true, planProductIds: [], allowedProducts: [] };

    const allowedProducts = promotionCode.coupon?.appliesToProducts || [];
    const allowedProductIds = promotionCode.coupon?.appliesToProductIds || [];
    if (!allowedProductIds.length) return { compatible: true, planProductIds: [], allowedProducts };
    if (pricingForm?.recurringPriceMode === 'CUSTOM' || pricingForm?.activationPriceMode === 'CUSTOM') {
      return { compatible: true, planProductIds: [], allowedProducts, verifiedByBackend: true };
    }

    const planVersion = plansCatalog
      .flatMap((plan) => plan.versions || [])
      .find((version) => version.id === planVersionId);
    const planProductIds = (planVersion?.prices || [])
      .filter((price) => !price.archivedAt)
      .flatMap((price) => (price.stripeMappings || [])
        .filter((mapping) => !mapping.archivedAt)
        .map((mapping) => mapping.stripeProductId))
      .filter(Boolean);

    return {
      compatible: allowedProductIds.some((productId) => planProductIds.includes(productId)),
      planProductIds,
      allowedProducts,
    };
  };

  const renderPromotionCodeSelector = (selectedId, onChange, basePlanVersionId, disabled = false, pricingForm = null) => {
    const selected = stripePromotionCodes.find((item) => item.id === selectedId);
    const compatibility = getPromotionCodeCompatibility(selectedId, basePlanVersionId, pricingForm);
    const discountLabel = selected?.coupon?.percentOff != null
      ? `${selected.coupon.percentOff}%`
      : selected?.coupon?.amountOff != null
        ? `${(selected.coupon.amountOff / 100).toFixed(2).replace('.', ',')} ${selected.coupon.currency || 'EUR'}`
        : '—';
    const durationLabel = selected?.coupon?.duration === 'forever'
      ? 'Per sempre'
      : selected?.coupon?.duration === 'repeating'
        ? `${selected.coupon.durationInMonths || 0} mesi`
        : 'Una sola volta';

    return (
      <div style={{ marginBottom: 14, border: selected && !compatibility.compatible ? '1px solid #f87171' : selected ? '1px solid #f9a8d4' : '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: selected && !compatibility.compatible ? '#fff7f7' : selected ? '#fff7fb' : '#ffffff' }}>
        <div style={{ fontWeight: 700, color: '#111827', fontSize: 14, marginBottom: 4 }}>Coupon Stripe automatico</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 9 }}>
          Se selezionato, il cliente lo troverà già compilato sotto la scelta del piano e non potrà sostituirlo.
        </div>
        <div style={{ marginBottom: 9, padding: '8px 10px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1e40af', fontSize: 12, lineHeight: 1.5 }}>
          <strong>Ricorda:</strong> il nome del coupon verrà visualizzato in fase di scelta del piano. Il nome si può modificare direttamente da Stripe nella sezione Catalogo Prodotti - Coupon.
        </div>
        <select
          value={selectedId}
          disabled={disabled}
          onChange={(event) => onChange('automaticPromotionCodeId', event.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: disabled ? '#f3f4f6' : '#ffffff', fontWeight: 600 }}
        >
          <option value="">Nessun coupon automatico</option>
          {stripePromotionCodes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.code}{item.coupon.name ? ` — ${item.coupon.name}` : ''} — {item.coupon.percentOff != null ? `${item.coupon.percentOff}%` : `${(item.coupon.amountOff / 100).toFixed(2)} ${item.coupon.currency || 'EUR'}`}
            </option>
          ))}
        </select>
        {selected && (
          <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 8, border: compatibility.compatible ? '1px solid #86efac' : '1px solid #fca5a5', background: compatibility.compatible ? '#f0fdf4' : '#fef2f2', color: compatibility.compatible ? '#166534' : '#b91c1c', fontSize: 12, fontWeight: 700 }}>
            {compatibility.compatible
              ? compatibility.verifiedByBackend ? 'La compatibilità verrà verificata sui prezzi Stripe personalizzati al salvataggio.' : 'Compatibile con il piano base selezionato.'
              : `Non compatibile con il piano base selezionato. Il coupon è valido solo per: ${compatibility.allowedProducts.map((product) => product.name).join(', ') || 'prodotti Stripe differenti'}.`}
          </div>
        )}
        {selected && (
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 8, padding: 10, border: '1px solid #fbcfe8', borderRadius: 8, background: '#ffffff', fontSize: 12, color: '#475569' }}>
            <div><strong style={{ color: '#111827' }}>Codice</strong><br />{selected.code}</div>
            <div style={{ gridColumn: 'span 2' }}><strong style={{ color: '#111827' }}>Nome</strong><br />{selected.coupon.name || 'Nessun nome'}</div>
            <div><strong style={{ color: '#111827' }}>Sconto</strong><br />{discountLabel}</div>
            <div><strong style={{ color: '#111827' }}>Durata</strong><br />{durationLabel}</div>
            <div><strong style={{ color: '#111827' }}>Scadenza</strong><br />{selected.expiresAt ? new Date(selected.expiresAt).toLocaleString('it-IT') : 'Nessuna'}</div>
            <div><strong style={{ color: '#111827' }}>Utilizzi</strong><br />{selected.timesRedeemed}{selected.maxRedemptions != null ? ` / ${selected.maxRedemptions}` : ' / illimitati'}</div>
            <div><strong style={{ color: '#111827' }}>Prima transazione</strong><br />{selected.restrictions?.firstTimeTransaction ? 'Sì' : 'No'}</div>
            <div><strong style={{ color: '#111827' }}>Importo minimo</strong><br />{selected.restrictions?.minimumAmount != null ? `${(selected.restrictions.minimumAmount / 100).toFixed(2)} ${selected.restrictions.minimumAmountCurrency}` : 'Nessuno'}</div>
            <div style={{ gridColumn: 'span 2' }}><strong style={{ color: '#111827' }}>Prodotti applicabili</strong><br />{selected.coupon.appliesToProducts?.length ? selected.coupon.appliesToProducts.map((product) => product.name).join(', ') : 'Tutti i prodotti'}</div>
          </div>
        )}
      </div>
    );
  };

  const renderOverrideEditor = (rows, setRows, disabled = false) => {
    if (!rows.length) {
      return <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 12 }}>Nessuna feature override disponibile per questo piano base.</p>;
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 12 }}>
        {rows.map((row) => (
          <div
            key={row.featureDefinitionId}
            style={{
              border: row.overrideEnabled ? '1px solid #f9a8d4' : '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 12,
              background: row.overrideEnabled ? 'linear-gradient(180deg, #fff7fb 0%, #ffffff 40%)' : '#ffffff',
              boxShadow: row.overrideEnabled ? '0 6px 20px rgba(194, 31, 122, 0.10)' : '0 1px 4px rgba(15, 23, 42, 0.04)',
              transition: 'all .18s ease',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: 14, marginBottom: 2 }}>{row.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 10.5, color: '#334155', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 7px' }}>{row.key}</span>
                  <span style={{ fontSize: 10.5, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 7px' }}>{row.valueType}</span>
                </div>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, overrideEnabled: !item.overrideEnabled } : item))}
                style={{
                  border: row.overrideEnabled ? '1px solid #c21f7a' : '1px solid #cbd5e1',
                  background: row.overrideEnabled ? '#c21f7a' : '#ffffff',
                  color: row.overrideEnabled ? '#ffffff' : '#334155',
                  borderRadius: 999,
                  padding: '5px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.overrideEnabled ? 'Override attivo' : 'Attiva override'}
              </button>
            </div>

            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              Base: {row.valueType === 'BOOLEAN' ? renderBooleanStatus(row.baseValue, true) : <span style={{ color: '#334155', fontWeight: 700 }}>{formatFeatureBaseValue(row)}</span>}
            </div>

            {row.overrideEnabled && (
              <div style={{ border: '1px dashed #f3b3d8', borderRadius: 10, background: '#fff8fc', padding: 10 }}>
                {row.valueType === 'BOOLEAN' ? (
                  <BooleanToggle
                    value={row.overrideValue}
                    disabled={disabled}
                    onChange={(nextValue) => setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, overrideValue: nextValue } : item))}
                  />
                ) : row.key === 'SOONTIP_FEE' ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Percentuale (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      disabled={disabled}
                      value={Number((row.overrideValue && typeof row.overrideValue === 'object' ? row.overrideValue.feePercent : 20) ?? 0)}
                      onChange={(e) => setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, overrideValue: { ...(item.overrideValue || { feePercent: 20, feeFixedCents: 0 }), feePercent: parseInt(e.target.value || 0, 10) } } : item))}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#ffffff' }}
                    />
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Importo fisso (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={disabled}
                      value={(((row.overrideValue && typeof row.overrideValue === 'object' ? row.overrideValue.feeFixedCents : 0) || 0) / 100).toFixed(2)}
                      onChange={(e) => {
                        const cents = Math.round(parseFloat(e.target.value || 0) * 100);
                        setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, overrideValue: { ...(item.overrideValue || { feePercent: 20, feeFixedCents: 0 }), feeFixedCents: cents } } : item));
                      }}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#ffffff' }}
                    />
                  </div>
                ) : row.valueType === 'JSON' ? (
                  <textarea
                    rows={5}
                    disabled={disabled}
                    value={row.jsonText}
                    onChange={(e) => setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, jsonText: e.target.value } : item))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', background: '#ffffff' }}
                  />
                ) : (
                  <div>
                    <input
                      type="number"
                      min="0"
                      step={row.valueType === 'FLOAT' ? '0.01' : '1'}
                      disabled={disabled}
                      value={row.overrideValue ?? ''}
                      placeholder="Illimitato"
                      onChange={(e) => setRows(prev => prev.map(item => item.featureDefinitionId === row.featureDefinitionId ? { ...item, overrideValue: e.target.value } : item))}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#ffffff' }}
                    />
                    <div style={{ marginTop: 6, fontSize: 11.5, color: row.overrideValue == null || row.overrideValue === '' || Number(row.overrideValue) === 0 ? '#166534' : '#64748b', fontWeight: row.overrideValue == null || row.overrideValue === '' || Number(row.overrideValue) === 0 ? 700 : 500 }}>
                      {row.overrideValue == null || row.overrideValue === '' || Number(row.overrideValue) === 0
                        ? 'Valore attuale: Illimitato (0 o campo vuoto)'
                        : '0 o campo vuoto = Illimitato'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => (
    <span style={{ marginLeft: '4px', fontSize: '10px', color: sortBy === col ? '#C21F7A' : '#d1d5db', lineHeight: 1 }}>
      {sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );

  const displayedOffers = [...(detailTab === 'legacy-readonly' ? legacyOffers : offers)]
    .filter(o => filterStatus === 'active' ? o.active : filterStatus === 'inactive' ? !o.active : true)
    .filter(o => !searchQuery.trim() || o.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'label') cmp = a.label.localeCompare(b.label, 'it');
      else if (sortBy === 'firmati') cmp = (a._count?.planContractOffers ?? a._count?.contractOffers ?? 0) - (b._count?.planContractOffers ?? b._count?.contractOffers ?? 0);
      else if (sortBy === 'fee') { const af = a.feeOverridePercent ?? a.feePercent ?? -1; const bf = b.feeOverridePercent ?? b.feePercent ?? -1; cmp = af !== bf ? af - bf : ((a.feeOverrideFixedCents ?? a.feeFixedCents ?? 0) - (b.feeOverrideFixedCents ?? b.feeFixedCents ?? 0)); }
      else if (sortBy === 'trial') {
        const aTrial = a.__source === 'legacy' ? (a.trialDays ?? 0) : (a.trialDaysOverride ?? a.basePlanVersion?.trialDays ?? 0);
        const bTrial = b.__source === 'legacy' ? (b.trialDays ?? 0) : (b.trialDaysOverride ?? b.basePlanVersion?.trialDays ?? 0);
        cmp = aTrial - bTrial;
      }
      else if (sortBy === 'scadenza') {
        const aT = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
        const bT = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
        cmp = aT - bT;
      } else if (sortBy === 'stato') cmp = (b.active ? 1 : 0) - (a.active ? 1 : 0);
      else if (sortBy === 'createdAt') cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // ── RENDER ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
        <p>Caricamento offerte commerciali...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <p style={{ color: 'red' }}>Errore: {error}</p>
        <button onClick={loadOffers}>Riprova</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Offerte Commerciali</h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
            Gestisci i piani tariffari e i link di invito per nuovi tenant
          </p>
        </div>
        <button
          onClick={toggleCreateForm}
          style={{ padding: '10px 20px', backgroundColor: '#C21F7A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
        >
          {showCreateForm ? 'Annulla' : '+ Nuova Offerta'}
        </button>
      </div>

      {/* ── Guida rapida ─────────────────────────────────────────── */}
      <details style={{ marginBottom: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#334155', userSelect: 'none' }}>
          ℹ️ Come funzionano le Offerte Commerciali?
        </summary>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13, color: '#475569', lineHeight: 1.65 }}>

          <div>
            <strong style={{ fontSize: 13.5 }}>🧾 A cosa servono</strong>
            <p style={{ margin: '6px 0 0' }}>
              Le <strong>offerte commerciali</strong> definiscono le condizioni proposte a un nuovo cliente: commissione percentuale, eventuale quota fissa,
              giorni di prova, prezzi Stripe ricorrenti e di attivazione, coupon automatico, data di scadenza e note contrattuali. In pratica, ogni offerta parte da una versione pubblicata di un piano e può ereditarne le condizioni oppure sostituirne selettivamente alcune.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>➕ Nuova offerta — cosa significano i campi</strong>
            <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Campo</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Significato pratico</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Label</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Il nome dell’offerta. È il testo con cui la riconoscerai e che il cliente potrà vedere.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Fee %</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>La percentuale applicata al servizio.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Fee fissa</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Un costo fisso aggiuntivo, espresso in centesimi.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Giorni trial</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Il periodo di prova iniziale, se previsto.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Canone ricorrente</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Può ereditare i Price SUBSCRIPTION del piano base oppure usare Price Stripe personalizzati selezionati dal catalogo.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Costo di attivazione</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Può essere ereditato dal piano base, escluso del tutto oppure sostituito con un Price Stripe una tantum.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Coupon Stripe automatico</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Promotion Code applicato automaticamente, dopo verifica della compatibilità con i Product effettivi dell’offerta.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Scadenza offerta</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Entro quando l’offerta può essere accettata. Se non la imposti, l’offerta resta disponibile finché non viene disattivata.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Condizioni particolari e note</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Informazioni aggiuntive utili da ricordare o da riportare nel flusso contrattuale.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>Prezzi Stripe personalizzati e Product</strong>
            <ul style={{ margin: '6px 0 0 16px' }}>
              <li><strong>Selezione esatta:</strong> il checkbox del Product seleziona in blocco i suoi Price compatibili; i checkbox figli consentono di scegliere soltanto i Price desiderati. SoonTip salva e verifica gli ID selezionati, senza espandere implicitamente l’intero Product.</li>
              <li><strong>Canone personalizzato:</strong> tutti i Price ricorrenti devono appartenere allo stesso Product Stripe. Sono ammessi più intervalli, per esempio mensile e annuale, ma un solo Price per ciascuna periodicità.</li>
              <li><strong>Attivazione personalizzata:</strong> deve essere un Price <code>one_time</code> e può appartenere a un Product diverso da quello del canone, perché rappresenta un ruolo economico distinto.</li>
              <li><strong>Valuta:</strong> i Price personalizzati dell’offerta devono usare una valuta coerente; il backend ricontrolla Product, tipo, stato, importo, periodicità e valuta al salvataggio.</li>
              <li><strong>Coupon:</strong> la compatibilità del Promotion Code viene verificata sui Product effettivamente ereditati o selezionati, non soltanto sul piano base.</li>
            </ul>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
              Se provi a selezionare un Price ricorrente di un altro Product, il picker blocca subito l’operazione. Il backend applica comunque lo stesso controllo anche alle richieste inviate direttamente via API.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🔗 Link di invito, scadenze e checkbox</strong>
            <ul style={{ margin: '6px 0 0 16px' }}>
              <li>Ogni offerta può avere <strong>uno o più link di invito</strong>, così puoi inviarla a destinatari diversi.</li>
              <li><strong>Max utilizzi</strong>: limita quante volte quel link può essere usato. Se lasci vuoto, gli utilizzi sono illimitati.</li>
              <li><strong>Validità (giorni)</strong>: indica fino a quando il link resta valido.</li>
              <li>La checkbox <strong>“Nessuna scadenza”</strong> significa che il link non ha una sua data di scadenza. In questo caso il campo dei giorni viene ignorato.</li>
              <li>Anche se il link non scade, <strong>non potrà più essere usato</strong> se l’offerta viene disattivata, revocata o arriva alla sua scadenza generale.</li>
              <li>Il link può essere <strong>copiato</strong>, <strong>visualizzato</strong> oppure <strong>inviato via email</strong> con un messaggio personalizzabile.</li>
            </ul>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🔒 Regole di accesso</strong>
            <ul style={{ margin: '6px 0 0 16px' }}>
              <li><strong>Regole Accesso (8)</strong> indica quante persone o email sono abilitate a usare quell’offerta.</li>
              <li>Nella ricerca puoi digitare <strong>nome, cognome o email</strong>.</li>
              <li>Se la persona <strong>esiste già nel sistema</strong>, puoi selezionarla dall’elenco.</li>
              <li>Se <strong>non esiste ancora</strong>, puoi comunque aggiungere direttamente la sua email: il link sarà riservato a quell’indirizzo.</li>
              <li>Quando è presente <strong>almeno una regola di accesso</strong>, l’offerta non è più aperta a tutti.</li>
              <li>Proprio per questo, nella lista vedrai anche il <strong>lucchetto 🔒 vicino al nome dell’offerta</strong>: è un promemoria visivo che l’offerta è limitata solo ai destinatari autorizzati.</li>
            </ul>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>✏️ Cosa succede se modifichi un’offerta dopo</strong>
            <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Situazione</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Cosa succede</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Nessun contratto ancora firmato</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>L’offerta può essere aggiornata normalmente.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Contratti in attesa di firma (<code>awaiting</code>)</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Se cambi i valori economici (fee, trial, Price o coupon), i documenti in attesa vengono annullati automaticamente e il cliente riceverà la versione aggiornata al prossimo accesso.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>Contratti già firmati (<code>signed</code>)</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>I campi economici vengono bloccati per non alterare condizioni già accettate. Restano modificabili solo le informazioni non economiche, dove consentito.</td>
                </tr>
              </tbody>
            </table>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
              Nella colonna <strong>“Firmati”</strong> vedi quanti contratti risultano già conclusi con quella offerta: è il numero da controllare prima di fare modifiche importanti.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🗑️ Quando puoi disattivare o eliminare un’offerta</strong>
            <ul style={{ margin: '6px 0 0 16px' }}>
              <li>Un’offerta <strong>default</strong> non può essere rimossa subito: prima bisogna impostarne un’altra come default.</li>
              <li>Se un’offerta ha già <strong>contratti firmati</strong>, il sistema tutela lo storico: in questi casi di norma puoi <strong>disattivarla</strong>, ma non eliminarla definitivamente.</li>
              <li>Se non ci sono contratti firmati collegati, il modale di eliminazione può consentire anche la <strong>rimozione definitiva</strong>.</li>
              <li>Quando un’offerta viene <strong>disattivata</strong>, non può più essere usata per nuove attivazioni e gli eventuali percorsi ancora in attesa vengono chiusi secondo le regole di sicurezza del sistema.</li>
            </ul>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🔄 Come funziona il nuovo sistema plan-based</strong>
            <ol style={{ margin: '6px 0 0 16px', lineHeight: 1.7, fontSize: 13 }}>
              <li>Selezioni un <strong>Piano Base</strong> (PlanVersion pubblicata).</li>
              <li>Applichi eventuali <strong>override</strong> (fee, limiti, feature, trial, Price ricorrenti o attivazione).</li>
              <li>Il cliente vede il risultato finale: <strong>piano derivato</strong>.</li>
              <li>Alla firma, salviamo nello <strong>snapshot risolto</strong> feature e condizioni economiche effettive, inclusi Price ID e Product ID, per audit e stabilità nel tempo.</li>
            </ol>
            <table style={{ marginTop: 10, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Modello</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Ruolo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>PlanCommercialOffer.basePlanVersionId</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Piano base obbligatorio — definisce fee e feature di default.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>PlanCommercialOfferFeatureOverride</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Override applicati sopra il piano base per questa specifica offerta.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>PlanCommercialOfferPriceOverride</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Conserva i Price e Product Stripe personalizzati per ruolo e ambiente, senza modificare i PlanPrice del piano base.</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>PlanContractOffer (snapshot)</td>
                  <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>Congela le condizioni risolte al momento della firma per audit e stabilità.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#0f4c81' }}>
            <strong>💡 Nota importante sulle fee:</strong>
            <div style={{ marginTop: 6, lineHeight: 1.7 }}>
              Quando un cliente firma un contratto con una determinata offerta, le <strong>fee concordate in quel contratto</strong> diventano il riferimento per quel tenant. Di conseguenza, i <strong>nuovi locali creati successivamente</strong> all’interno di quel tenant verranno configurati automaticamente con quelle stesse fee, senza doverle reinserire ogni volta manualmente.
            </div>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🏷️ Legenda rapida</strong>
            <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                <strong>INATTIVA</strong>: l’offerta esiste ancora nello storico, ma non può più essere usata per nuove attivazioni.
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                <strong>Attive / Inattive / Tutte</strong>: servono solo per filtrare la lista e trovare più velocemente le offerte che ti interessano.
              </div>
            </div>
          </div>
        </div>
      </details>

      {/* Create Form */}
      {showCreateForm && (
        <div style={{ background: 'linear-gradient(180deg, #ffffff 0%, #fcfcff 100%)', border: '1px solid #e9d5ff', borderRadius: '14px', marginBottom: '24px', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', background: 'linear-gradient(90deg, #faf5ff 0%, #fff1f2 100%)' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#9d174d', background: 'rgba(255,255,255,0.85)', border: '1px solid #fbcfe8', borderRadius: '999px', padding: '4px 10px' }}>
              ✨ Nuova offerta
            </div>
            <h3 style={{ margin: '10px 0 6px', fontSize: '20px', color: '#111827' }}>Crea una nuova offerta commerciale</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#5b6473', lineHeight: 1.6 }}>
              Definisci piano base, override funzionali e prezzi Stripe personalizzati dell&apos;offerta.
            </p>
          </div>

          <form onSubmit={handleCreate} style={{ padding: '20px' }}>
            {formError && (
              <div style={{ marginBottom: '16px', padding: '10px 12px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '13px' }}>
                {formError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', alignItems: 'start' }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', background: '#ffffff' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#7c3aed', marginBottom: '12px' }}>
                  Dettagli offerta
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>
                    Label *
                  </label>
                  <input
                    required
                    autoFocus
                    value={formData.label}
                    onChange={e => setFormData(p => ({ ...p, label: e.target.value }))}
                    placeholder="Es: Piano Standard, Promo apertura, Offerta Enterprise..."
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                  />
                  <p style={{ margin: '6px 0 0', fontSize: '11.5px', color: '#6b7280', lineHeight: 1.5 }}>
                    Questo nome sarà visibile anche al cliente nel suo percorso di attivazione.
                  </p>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>
                    Scadenza offerta
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={e => setFormData(p => ({ ...p, expiresAt: e.target.value }))}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#fff' }}
                  />
                  <p style={{ margin: '6px 0 0', fontSize: '11.5px', color: '#6b7280' }}>
                    Lascia vuoto se vuoi mantenerla disponibile fino a disattivazione manuale.
                  </p>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>
                    Condizioni particolari e note
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                    rows={4}
                    placeholder="Es: condizioni speciali, clausole aggiuntive, note utili per il contratto..."
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#fff', resize: 'vertical' }}
                  />
                </div>

                <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <AccessRulesEditor
                    title={`Regole Accesso (${createAccessRules.length})`}
                    rules={createAccessRules}
                    searchState={createAccessSearch}
                    setSearchState={setCreateAccessSearch}
                    onSearchChange={handleCreateAccessSearchChange}
                    onAddEntry={handleAddDraftAccessEntry}
                    onRemoveRule={handleRemoveDraftAccessRule}
                    emptyText="Nessuna restrizione — se lasci vuoto, l’offerta sarà aperta a tutti."
                    helperText="Puoi limitare fin da subito l’offerta a persone o email specifiche."
                  />
                </div>

              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', background: '#fbfdff' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#2563eb', marginBottom: '12px' }}>
                  Gestione override funzionali
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, fontSize: '13px', color: '#1f2937' }}>
                    Piano base *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      required
                      value={formData.basePlanVersionId}
                      onChange={e => handleCreateBasePlanChange(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '11px 38px 11px 12px', border: '1px solid #93c5fd', borderRadius: '10px', fontSize: '14px', fontWeight: 600, color: '#1e3a8a', background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                    >
                      {planVersionOptions.length === 0 && <option value="">Nessun piano pubblicato disponibile</option>}
                      {planVersionOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#2563eb', pointerEvents: 'none', fontSize: '12px' }}>▾</span>
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: '11.5px', color: '#6b7280', lineHeight: 1.5 }}>
                    Seleziona la PlanVersion pubblicata da cui partire.
                  </p>
                  {(() => {
                    const selectedPlanVersion = plansCatalog.flatMap((plan) => plan.versions || []).find((version) => version.id === formData.basePlanVersionId);
                    return renderPlanPriceSummary(selectedPlanVersion);
                  })()}
                </div>

                <div style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '8px', background: '#eff6ff', border: '1px dashed #93c5fd', color: '#1e3a8a', fontSize: '12px' }}>
                  Quando cambi piano base, i valori mostrati qui si ricalcolano dal nuovo piano.
                </div>

                {renderPriceOverrideCard(formData, setFormData)}
                {renderFeeOverrideCard(
                  formData.feeOverridePercent, formData.feeOverrideFixedCents,
                  (field, val) => setFormData(p => ({ ...p, [field]: val })),
                  formData.basePlanVersionId
                )}
                {renderTrialOverrideCard(
                  formData.trialDaysOverride,
                  (field, val) => setFormData(p => ({ ...p, [field]: val })),
                  formData.basePlanVersionId
                )}
                {renderPromotionCodeSelector(
                  formData.automaticPromotionCodeId,
                  (field, val) => {
                    setFormCouponNotice('');
                    setFormData(p => ({ ...p, [field]: val }));
                  },
                  formData.basePlanVersionId,
                  false,
                  formData
                )}
                {formCouponNotice && (
                  <div style={{ margin: '-4px 0 14px', padding: '9px 11px', borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600 }}>
                    {formCouponNotice}
                  </div>
                )}
                {renderOverrideEditor(createOverrideRows, setCreateOverrideRows)}
              </div>
            </div>

            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                I campi contrassegnati con * sono obbligatori.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={openCreateContractPreview}
                  style={{ padding: '10px 16px', border: '1px solid #93c5fd', borderRadius: '8px', cursor: 'pointer', background: '#eff6ff', color: '#1d4ed8' }}
                >
                  📄 Anteprima contratto
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  style={{ padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', background: '#fff', color: '#374151' }}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting || !getPromotionCodeCompatibility(formData.automaticPromotionCodeId, formData.basePlanVersionId, formData).compatible}
                  style={{ padding: '10px 18px', backgroundColor: '#C21F7A', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 6px 16px rgba(194, 31, 122, 0.22)' }}
                >
                  {formSubmitting ? 'Creazione...' : 'Crea offerta'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Filter bar — above both columns */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '2px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '3px', background: '#f9fafb' }}>
          {[['all', 'Tutte'], ['active', 'Attive'], ['inactive', 'Inattive']].map(([val, label]) => (
            <button key={val} onClick={() => setFilterStatus(val)} style={{
              padding: '4px 12px', fontSize: '13px', border: 'none', borderRadius: '4px', cursor: 'pointer',
              background: filterStatus === val ? '#C21F7A' : 'transparent',
              color: filterStatus === val ? 'white' : '#374151',
              fontWeight: filterStatus === val ? 600 : 400,
              transition: 'all 0.15s'
            }}>
              {label}
              <span style={{ marginLeft: '5px', fontSize: '11px', opacity: 0.75 }}>
                {val === 'all' ? offers.length : val === 'active' ? offers.filter(o => o.active).length : offers.filter(o => !o.active).length}
              </span>
            </button>
          ))}
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Cerca offerta..."
          style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '6px', outline: 'none', width: '200px' }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button
          onClick={() => setDetailTab('new-system')}
          style={{
            border: detailTab === 'new-system' ? '1px solid #2563eb' : '1px solid #d1d5db',
            background: detailTab === 'new-system' ? '#eff6ff' : '#fff',
            color: detailTab === 'new-system' ? '#1d4ed8' : '#374151',
            borderRadius: 999,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >Sistema plan-based ({offers.length})</button>
        <button
          onClick={() => setDetailTab('legacy-readonly')}
          style={{
            border: detailTab === 'legacy-readonly' ? '1px solid #b45309' : '1px solid #d1d5db',
            background: detailTab === 'legacy-readonly' ? '#fffbeb' : '#fff',
            color: detailTab === 'legacy-readonly' ? '#92400e' : '#374151',
            borderRadius: 999,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >Legacy (read-only) ({legacyOffers.length})</button>
      </div>

      {/* Offers Table + Detail (split layout) */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedOffer ? 'minmax(0, 1.45fr) minmax(360px, 0.85fr)' : '1fr', gap: '20px', alignItems: 'stretch' }}>

        {/* Offers List */}
        <div>
          {displayedOffers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666', border: '1px dashed #ccc', borderRadius: '8px' }}>
              {(detailTab === 'legacy-readonly' ? legacyOffers.length : offers.length) === 0
                ? detailTab === 'legacy-readonly'
                  ? <><p>Nessuna offerta legacy disponibile.</p><p>I dati legacy sono visibili solo in modalità read-only.</p></>
                  : <><p>Nessuna offerta commerciale creata.</p><p>Crea la prima offerta cliccando "+ Nuova Offerta".</p></>
                : <p>Nessuna offerta corrisponde al filtro selezionato.</p>}
            </div>
          ) : (
            <div ref={tableScrollRef} style={{ maxHeight: '480px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th
                    onClick={() => toggleSort('createdAt')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'createdAt' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Creata{sortIcon('createdAt')}</span></th>
                  <th
                    onClick={() => toggleSort('label')}
                    style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'label' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Label{sortIcon('label')}</span></th>
                  <th
                    onClick={() => toggleSort('fee')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'fee' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Fee{sortIcon('fee')}</span></th>
                  <th
                    onClick={() => toggleSort('trial')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'trial' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Trial{sortIcon('trial')}</span></th>
                  <th
                    onClick={() => toggleSort('firmati')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'firmati' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Firmati{sortIcon('firmati')}</span></th>
                  <th
                    onClick={() => toggleSort('scadenza')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'scadenza' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Scadenza{sortIcon('scadenza')}</span></th>
                  <th
                    onClick={() => toggleSort('stato')}
                    style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', cursor: 'pointer', userSelect: 'none', background: sortBy === 'stato' ? '#e8d5f0' : '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}
                  ><span style={{ display: 'inline-flex', alignItems: 'center' }}>Stato{sortIcon('stato')}</span></th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', background: '#f0f0f0', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {displayedOffers.map(offer => (
                  <tr
                    key={offer.id}
                    onClick={() => loadOfferDetail(offer.id, detailTab === 'legacy-readonly' ? 'legacy' : 'new')}
                    style={{
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      background: selectedOffer?.id === offer.id ? '#fff0f8' : 'white'
                    }}
                  >
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(offer.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      <br />
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {new Date(offer.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {(offer._count?.accessRules ?? 0) > 0 && (
                          <span
                            title={`Accesso limitato — ${offer._count.accessRules} ${offer._count.accessRules === 1 ? 'regola attiva' : 'regole attive'}`}
                            style={{ fontSize: '13px', cursor: 'default', lineHeight: 1 }}
                          >🔒</span>
                        )}
                        <strong>{offer.label}</strong>
                        {!offer.active && (
                          <span style={{ fontSize: '11px', background: '#9ca3af', color: 'white', padding: '1px 6px', borderRadius: '10px' }}>INATTIVA</span>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {(() => { const pct = offer.feeOverridePercent ?? offer.feePercent; const fixed = offer.feeOverrideFixedCents ?? offer.feeFixedCents ?? 0; return pct != null ? `${pct}%${fixed > 0 ? ` + ${formatCents(fixed)}` : ''}` : '(piano base)'; })()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {(() => { const days = offer.__source === 'legacy' ? (offer.trialDays ?? 0) : (offer.trialDaysOverride ?? offer.basePlanVersion?.trialDays ?? 0); return days > 0 ? `${days}gg${offer.__source === 'new' && offer.trialDaysOverride != null ? ' (override)' : ''}` : '—'; })()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openSignedContractsModal(offer.id, detailTab === 'legacy-readonly' ? 'legacy' : 'new');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: (offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) > 0 ? 'pointer' : 'default',
                          color: (offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) > 0 ? '#2563eb' : '#6b7280',
                          textDecoration: (offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) > 0 ? 'underline' : 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          padding: 0,
                          opacity: (offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) > 0 ? 1 : 0.6
                        }}
                        disabled={(offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) === 0}
                        title={(offer._count?.planContractOffers ?? offer._count?.contractOffers ?? 0) > 0 ? 'Clicca per vedere i tenant che hanno firmato' : 'Nessun contratto firmato'}
                      >
                        {offer._count?.planContractOffers ?? offer._count?.contractOffers ?? '—'}
                      </button>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px' }}>
                      {offer.expiresAt
                        ? <span style={{ color: new Date(offer.expiresAt) < new Date() ? '#dc2626' : '#374151' }}>
                            {new Date(offer.expiresAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                        : <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Nessuna</span>}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                        background: offer.active ? '#dcfce7' : '#f3f4f6',
                        color: offer.active ? '#15803d' : '#6b7280'
                      }}>
                        {offer.active ? 'Attiva' : 'Inattiva'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        <button
                          onClick={() => { setInvitationModal({ open: true, offerId: offer.id }); setInvitationResult(null); }}
                          title="Crea link invito"
                          style={{ width: '30px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #C21F7A', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#C21F7A', flexShrink: 0 }}
                        >🔗</button>
                        {offer.active && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Disattivare questa offerta? Non potrà più essere usata per nuove attivazioni.')) return;
                              try {
                                await commercialOfferService.deactivate(offer.id);
                                loadOffers();
                                if (selectedOffer?.id === offer.id) setSelectedOffer(null);
                              } catch (err) {
                                alert('Errore: ' + (err.response?.data?.error || err.message));
                              }
                            }}
                            title="Disattiva offerta"
                            style={{ width: '30px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #f59e0b', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#d97706', flexShrink: 0 }}
                          >⏸</button>
                        )}
                        {offer.active && (
                          <button
                            onClick={() => openDeleteModal(offer.id)}
                            title="Elimina / Disattiva offerta"
                            style={{ width: '30px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #ef4444', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#ef4444', flexShrink: 0 }}
                          >🗑</button>
                        )}
                        {!offer.active && (
                          <button
                            onClick={() => handleReactivate(offer.id)}
                            title="Riattiva offerta"
                            style={{ width: '30px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #16a34a', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#16a34a', flexShrink: 0 }}
                          >✓</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Offer Detail Panel */}
        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '20px', maxHeight: '480px', overflowY: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: '460px', justifySelf: 'end' }}>
          {selectedOffer ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>{selectedOffer.label}</h3>
                <button
                  onClick={() => openContractPreview(selectedOffer)}
                  title="Anteprima contratto"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #93c5fd',
                    background: '#eff6ff',
                    cursor: 'pointer',
                    color: '#1d4ed8',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 400,
                    boxShadow: '0 1px 2px rgba(37, 99, 235, 0.08)'
                  }}
                >📄 Anteprima contratto</button>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {detailTab !== 'legacy-readonly' && selectedOffer?.__source !== 'legacy' && (
                  <button
                    onClick={() => openEditModal(selectedOffer)}
                    title="Modifica offerta"
                    style={{ border: '1px solid #2563eb', background: 'white', cursor: 'pointer', color: '#2563eb', borderRadius: '6px', padding: '4px 10px', fontSize: '14px' }}
                  >✏️</button>
                )}
                <button onClick={() => setSelectedOffer(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', color: '#666' }}>✕</button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#374151' }}>Dettagli Offerta</h3>
              <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 13 }}>
                Seleziona un'offerta dalla tabella per vedere i dettagli completi.
              </p>
            </div>
          )}

            <div style={{ marginBottom: 14, borderBottom: '1px solid #e5e7eb', paddingBottom: 10, fontSize: 12, color: '#64748b' }}>
              Vista corrente: <strong>{detailTab === 'legacy-readonly' ? 'Legacy (read-only)' : 'Sistema plan-based'}</strong>
            </div>

            {detailLoading ? (
              <p>Caricamento...</p>
            ) : (
              <>
                {detailTab === 'new-system' ? (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#1e3a8a' }}>
                      {selectedOffer
                        ? 'Questa offerta usa il nuovo flusso plan-based: Piano Base + Override risolti lato backend.'
                        : 'Nuovo sistema attivo: le offerte commerciali si basano su Piano Base + Override risolti lato backend.'}
                    </div>

                    {selectedOffer?.basePlanVersion && (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Piano base</div>
                        <div style={{ fontWeight: 700, color: '#111827' }}>
                          {selectedOffer.basePlanVersion.plan?.name || selectedOffer.basePlanVersion.plan?.code || '—'}
                          <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>v{selectedOffer.basePlanVersion.version}</span>
                          <span style={{ marginLeft: 8, fontSize: 11, background: selectedOffer.basePlanVersion.status === 'PUBLISHED' ? '#dcfce7' : '#f1f5f9', color: selectedOffer.basePlanVersion.status === 'PUBLISHED' ? '#166534' : '#475569', borderRadius: 999, padding: '2px 7px' }}>{selectedOffer.basePlanVersion.status}</span>
                        </div>
                      </div>
                    )}

                    {selectedOffer && (
                      <div style={{ background: '#f8fbff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 7 }}>Prezzi effettivi dell&apos;offerta</div>
                        <div style={{ marginBottom: 5 }}>
                          <strong>Ricorrente:</strong>{' '}
                          {selectedOffer.recurringPriceMode === 'CUSTOM' ? 'Personalizzato' : 'Come piano base'}
                        </div>
                        {selectedOffer.recurringPriceMode === 'CUSTOM' && (selectedOffer.priceOverrides || [])
                          .filter((price) => price.purpose === 'SUBSCRIPTION')
                          .map((price) => (
                            <div key={price.id} style={{ margin: '3px 0 3px 12px', color: '#334155' }}>
                              {formatCents(price.amountCents)} / {price.intervalCount > 1 ? `${price.intervalCount} ` : ''}{price.interval}
                              {' '}<code style={{ color: '#64748b', fontSize: 11 }}>{price.stripePriceId}</code>
                            </div>
                          ))}
                        <div style={{ marginTop: 7 }}>
                          <strong>Attivazione:</strong>{' '}
                          {selectedOffer.activationPriceMode === 'NONE'
                            ? 'Nessun costo'
                            : selectedOffer.activationPriceMode === 'CUSTOM'
                              ? 'Personalizzata'
                              : 'Come piano base'}
                        </div>
                        {selectedOffer.activationPriceMode === 'CUSTOM' && (selectedOffer.priceOverrides || [])
                          .filter((price) => price.purpose === 'ACTIVATION')
                          .map((price) => (
                            <div key={price.id} style={{ margin: '3px 0 0 12px', color: '#334155' }}>
                              {formatCents(price.amountCents)} una tantum{' '}
                              <code style={{ color: '#64748b', fontSize: 11 }}>{price.stripePriceId}</code>
                            </div>
                          ))}
                      </div>
                    )}

                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ color: '#111827' }}>Preview diff simulata (Piano Base vs Override)</strong>
                        <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '3px 8px' }}>Simulazione UX</span>
                      </div>
                      <p style={{ margin: '6px 0 10px', color: '#6b7280', fontSize: 12 }}>
                        {selectedOffer
                          ? 'Questa tabella mostra il confronto simulato tra base plan e override per l\'offerta selezionata.'
                          : 'Seleziona un\'offerta per visualizzare il confronto simulato tra base plan e override.'}
                      </p>

                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ textAlign: 'left', border: '1px solid #e2e8f0', padding: '6px 8px' }}>Voce</th>
                            <th style={{ textAlign: 'left', border: '1px solid #e2e8f0', padding: '6px 8px' }}>Piano Base</th>
                            <th style={{ textAlign: 'left', border: '1px solid #e2e8f0', padding: '6px 8px' }}>Override offerta</th>
                            <th style={{ textAlign: 'center', border: '1px solid #e2e8f0', padding: '6px 8px' }}>Esito</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buildSimulatedDiffRows(selectedOffer || {}).map((row) => (
                            <tr key={row.label}>
                              <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px', fontWeight: 600 }}>{row.label}</td>
                              <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{row.baseValue}</td>
                              <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{row.overrideValue}</td>
                              <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px', textAlign: 'center' }}>
                                {row.changed
                                  ? <span style={{ fontSize: 11, background: '#fee2e2', color: '#b91c1c', borderRadius: 999, padding: '2px 8px' }}>Sovrascritto</span>
                                  : <span style={{ fontSize: 11, background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '2px 8px' }}>Ereditato</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <AccessRulesEditor
                        title={`Regole Accesso (${(selectedOffer?.accessRules || []).length})`}
                        rules={selectedOffer?.accessRules || []}
                        searchState={accessSearch}
                        setSearchState={setAccessSearch}
                        onSearchChange={handleAccessSearchChange}
                        onAddEntry={handleAddAccessEntry}
                        onRemoveRule={(rule) => handleRemoveAccess(selectedOffer.id, rule.id)}
                        emptyText="Nessuna restrizione — offerta aperta a tutti."
                        helperText="Aggiungi persone o email per limitare l'accesso a questa offerta."
                      />
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <strong>Link Invito ({(selectedOffer?.invitations || []).length})</strong>
                        <button
                          onClick={() => { setInvitationModal({ open: true, offerId: selectedOffer.id }); setInvitationResult(null); }}
                          style={{ padding: '4px 12px', fontSize: 12, background: '#fff', color: '#C21F7A', border: '1px solid #C21F7A', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                        >+ Link</button>
                      </div>
                      {(selectedOffer?.invitations || []).length === 0 ? (
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>Nessun link di invito attivo.</p>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {(selectedOffer?.invitations || []).map(inv => (
                            <div key={inv.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                              <div style={{ fontWeight: 600, marginBottom: 2 }}>{inv.label || 'Link senza nome'}</div>
                              <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                                Usi: {inv.uses}{inv.maxUses === -1 ? ' (illimitati)' : `/${inv.maxUses}`}
                                {inv.expiresAt ? ` · Scade: ${new Date(inv.expiresAt).toLocaleDateString('it-IT')}` : ' · Nessuna scadenza'}
                              </div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button onClick={() => setViewLinkModal({ open: true, inv })}
                                  style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 5, cursor: 'pointer' }}>👁</button>
                                <button onClick={() => openShareEmailModal(selectedOffer.id, inv, selectedOffer?.label || '', selectedOffer?.expiresAt || null)}
                                  style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 5, cursor: 'pointer' }}>💌</button>
                                <button onClick={() => navigator.clipboard.writeText(inv.invitationUrl)}
                                  style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #C21F7A', background: '#fff', color: '#C21F7A', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>📋 Copia</button>
                                <button onClick={() => handleDeleteInvitation(selectedOffer.id, inv.id)}
                                  style={{ padding: '4px 8px', border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#92400e' }}>
                      Modalità <strong>read-only</strong>: qui visualizzi i dati legacy correnti, senza modifiche da questo pannello.
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', fontSize: '13px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div><strong>Fee %:</strong> {selectedOffer ? (() => { const pct = selectedOffer.feeOverridePercent ?? selectedOffer.feePercent; return pct != null ? `${pct}%${selectedOffer.__source === 'new' && selectedOffer.feeOverridePercent != null ? ' (override)' : ''}` : '(usa fee piano base)'; })() : '—'}</div>
                      <div><strong>Fee fissa:</strong> {selectedOffer ? formatCents(selectedOffer.feeOverrideFixedCents ?? selectedOffer.feeFixedCents) : '—'}</div>
                      <div><strong>Trial:</strong> {selectedOffer ? (() => { const days = selectedOffer.__source === 'legacy' ? (selectedOffer.trialDays ?? 0) : (selectedOffer.trialDaysOverride ?? selectedOffer.basePlanVersion?.trialDays ?? 0); return `${days} giorni${selectedOffer.__source === 'new' && selectedOffer.trialDaysOverride != null ? ' (override)' : ' (piano base)'}`; })() : '—'}</div>
                      <div><strong>Setup:</strong> {selectedOffer ? formatCents(selectedOffer.setupFeeCents) : '—'}</div>
                      <div><strong>Abbonamento:</strong> {selectedOffer ? `${formatCents(selectedOffer.softwareSubscriptionFeeCents ?? 0)} / mese` : '—'}</div>
                      {selectedOffer?.notes && <div style={{ gridColumn: '1 / -1' }}><strong>Condizioni particolari e note:</strong> {selectedOffer.notes}</div>}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Scadenza offerta:</strong>{' '}
                        {selectedOffer?.expiresAt
                          ? <span style={{ color: new Date(selectedOffer.expiresAt) < new Date() ? '#dc2626' : 'inherit' }}>
                              {new Date(selectedOffer.expiresAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          : <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Nessuna scadenza</span>}
                      </div>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <strong>Tabelle legacy coinvolte</strong>
                      <div style={{ marginTop: 8, fontFamily: 'monospace', color: '#374151', display: 'grid', gap: 4 }}>
                        <div>CommercialOffer</div>
                        <div>CommercialOfferAccess</div>
                        <div>CommercialOfferInvitation</div>
                        <div>ContractOffer</div>
                        <div>ContractAcceptance</div>
                      </div>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <strong>Regole accesso (read-only)</strong>
                      {(selectedOffer?.accessRules?.length ?? 0) === 0 ? (
                        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>Nessuna restrizione — offerta aperta a tutti.</p>
                      ) : (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(selectedOffer?.accessRules || []).map(rule => (
                            <span key={rule.id || rule.userId || rule.email} style={{ fontSize: 12, border: '1px solid #e9d5ff', background: '#fdf4ff', color: '#581c87', borderRadius: 999, padding: '4px 8px' }}>
                              {rule.label || rule.email || rule.userId || '—'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, fontSize: 13 }}>
                      <strong>Link invito (read-only)</strong>
                      {(selectedOffer?.invitations || []).length === 0 ? (
                        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>Nessun link di invito attivo.</p>
                      ) : (
                        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                          {(selectedOffer?.invitations || []).map(inv => (
                            <div key={inv.id} style={{ border: '1px solid #f1f5f9', borderRadius: 6, padding: 8 }}>
                              <div style={{ fontWeight: 600 }}>{inv.label || 'Link senza nome'}</div>
                              <div style={{ color: '#555', marginTop: 4 }}>
                                Usi: {inv.uses}{inv.maxUses === -1 ? ' (illimitati)' : `/${inv.maxUses}`}
                                {inv.expiresAt ? ` · Scade: ${new Date(inv.expiresAt).toLocaleDateString('it-IT')}` : ' · Nessuna scadenza'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      </div>

      {/* Contract Preview Modal */}
      {contractPreview.open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1200,
            padding: '24px 12px', overflowY: 'auto'
          }}
          onClick={closeContractPreview}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: 'min(1200px, 96vw)',
              maxHeight: '92vh',
              overflow: 'hidden',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              margin: '0 auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: 0, color: '#0f172a' }}>{contractPreview.title || 'Anteprima contratto SoonTip'}</h4>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>
                  {contractPreview.offerLabel ? `Offerta: ${contractPreview.offerLabel}` : 'Simulazione contratto'}
                  {contractPreview.version ? ` · Versione ${contractPreview.version}` : ''}
                </p>
              </div>
              <button
                onClick={closeContractPreview}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', fontSize: '20px', lineHeight: 1 }}
                title="Chiudi anteprima"
              >
                ✕
              </button>
            </div>

            <div style={{ overflowY: 'auto', padding: '24px', background: '#f8fafc', flex: '1 1 auto', minHeight: 0, maxHeight: 'calc(92vh - 88px)', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
              {contractPreview.loading ? (
                <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '24px', borderRadius: '12px', textAlign: 'center', color: '#64748b' }}>
                  Caricamento anteprima contratto...
                </div>
              ) : contractPreview.error ? (
                <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fef2f2', border: '1px solid #fecaca', padding: '16px', borderRadius: '12px', color: '#b91c1c' }}>
                  {contractPreview.error}
                </div>
              ) : (
                <>
                  <div style={{ maxWidth: '900px', margin: '0 auto 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', color: '#1d4ed8' }}>
                    {contractPreview.note || 'Questa anteprima usa dati demo dove le informazioni reali non sono ancora note. Le condizioni economiche mostrate nell’Allegato A riflettono l’offerta selezionata.'}
                  </div>
                  <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '32px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)', overflowX: 'auto' }}>
                    <div dangerouslySetInnerHTML={{ __html: contractPreview.html }} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invitation Modal */}
      {invitationModal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '480px', maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 16px' }}>Crea Link di Invito</h3>
            {invitationResult ? (
              <>
                <div style={{ background: '#f0fff4', border: '1px solid #86efac', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#15803d' }}>✅ Link creato con successo!</p>
                  <input
                    readOnly
                    value={invitationResult.invitationUrl}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                    onClick={e => e.target.select()}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(invitationResult.invitationUrl)}
                      style={{ padding: '6px 12px', background: '#C21F7A', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      📋 Copia link
                    </button>
                    <button
                      onClick={() => openShareEmailModal(invitationModal.offerId, invitationResult, selectedOffer?.label || invitationResult?.offerLabel || '', selectedOffer?.expiresAt || invitationResult?.offerExpiresAt || null)}
                      style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      📧 Invia via email
                    </button>
                  </div>
                </div>
                <button onClick={() => { setInvitationModal({ open: false, offerId: null }); if (selectedOffer?.id === invitationModal.offerId) loadOfferDetail(invitationModal.offerId); }}
                  style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Chiudi
                </button>
              </>
            ) : (
              <form onSubmit={handleCreateInvitation}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Etichetta</label>
                  <input
                    value={invitationForm.label}
                    onChange={e => setInvitationForm(p => ({ ...p, label: e.target.value }))}
                    placeholder="Es: Invito per Ristorante Roma"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Max utilizzi</label>
                    <input
                      type="number"
                      min="1"
                      value={invitationForm.maxUses}
                      onChange={e => setInvitationForm(p => ({ ...p, maxUses: e.target.value }))}
                      placeholder="Illimitati"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Validità (giorni)</label>
                    <input
                      type="number"
                      min="1"
                      value={invitationForm.noExpiry ? '' : invitationForm.ttlDays}
                      onChange={e => setInvitationForm(p => ({ ...p, ttlDays: e.target.value }))}
                      disabled={invitationForm.noExpiry}
                      placeholder="Es: 30"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', background: invitationForm.noExpiry ? '#f5f5f5' : 'white' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={invitationForm.noExpiry}
                      onChange={e => setInvitationForm(p => ({ ...p, noExpiry: e.target.checked }))}
                    />
                    Nessuna scadenza
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" disabled={invitationSubmitting}
                    style={{ padding: '8px 20px', background: '#C21F7A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    {invitationSubmitting ? 'Creazione...' : 'Crea Link'}
                  </button>
                  <button type="button" onClick={() => setInvitationModal({ open: false, offerId: null })}
                    style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    Annulla
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Share Invitation Email Modal */}
      {shareEmailModal.open && shareEmailModal.invitation && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '760px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px' }}>Condividi offerta via email</h3>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: '13px' }}>
              Inserisci una o più email e personalizza solo il messaggio iniziale. Il pulsante <strong>Attiva Offerta</strong> e il link di fallback resteranno sempre inclusi.
            </p>

            <form onSubmit={handleSendInvitationEmail}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '16px' }}>
                <div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Email destinatari *</label>
                    <textarea
                      rows={4}
                      value={shareEmailModal.emails}
                      onChange={e => setShareEmailModal(prev => ({ ...prev, emails: e.target.value, error: '', success: '' }))}
                      placeholder={'esempio@azienda.it, commerciale@azienda.it\naltra@email.it'}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: '13px' }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Separa più email con una virgola, punto e virgola o una email per riga.
                    </p>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Messaggio iniziale personalizzabile</label>
                    <textarea
                      rows={8}
                      value={shareEmailModal.message}
                      onChange={e => setShareEmailModal(prev => ({ ...prev, message: e.target.value, error: '', success: '' }))}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: '13px' }}
                    />
                  </div>

                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#4b5563' }}>
                    <strong style={{ display: 'block', marginBottom: '6px', color: '#111827' }}>Sempre incluso nell'email</strong>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      <li>Pulsante CTA <strong>Attiva Offerta</strong></li>
                      <li>Link completo da copiare nel browser in caso di problemi</li>
                      <li>Branding SoonTip e riepilogo dell'offerta</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600 }}>Anteprima email</label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>Template base SoonTip</span>
                  </div>
                  <div style={{ background: '#f3f4f6', borderRadius: '12px', padding: '12px' }}>
                    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
                      <div style={{ padding: '18px', background: 'linear-gradient(135deg, #C21F7A 0%, #8b5cf6 100%)', color: 'white' }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, letterSpacing: '0.04em', textTransform: 'uppercase' }}>SoonTip</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, marginTop: '8px', lineHeight: 1.2 }}>C'è una nuova offerta commerciale per te</div>
                      </div>
                      <div style={{ padding: '18px' }}>
                        <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '10px', padding: '12px', color: '#9d174d', fontSize: '13px', lineHeight: 1.6 }}>
                          {(shareEmailModal.message.trim() || getDefaultShareMessage(shareEmailModal.offerLabel || shareEmailModal.invitation?.label || ''))
                            .split('\n')
                            .filter(line => line.trim() !== '')
                            .map((line, index) => (
                              <p key={index} style={{ margin: index === 0 ? '0 0 8px' : '0 0 8px' }}>{line}</p>
                            ))}
                        </div>

                        <div style={{ marginTop: '14px', padding: '12px', borderRadius: '10px', background: '#faf5ff', border: '1px solid #e9d5ff' }}>
                          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Offerta riservata</div>
                          <div style={{ fontSize: '15px', fontWeight: 700, color: '#1f1135', marginTop: '4px' }}>{shareEmailModal.offerLabel || shareEmailModal.invitation?.label || 'Offerta SoonTip'}</div>
                          {shareEmailModal.offerExpiresAt && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                              {`Offerta attivabile entro: ${new Date(shareEmailModal.offerExpiresAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                            </div>
                          )}
                        </div>

                        <div style={{ textAlign: 'center', margin: '18px 0 14px' }}>
                          <span style={{ display: 'inline-block', padding: '11px 18px', borderRadius: '999px', background: 'linear-gradient(135deg, #C21F7A 0%, #8b5cf6 100%)', color: 'white', fontWeight: 700, fontSize: '13px' }}>
                            Attiva Offerta
                          </span>
                        </div>

                        <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6b7280' }}>
                          Se non visualizzi il pulsante, copia e incolla questo link nel browser:
                        </p>
                        <div style={{ fontSize: '12px', color: '#C21F7A', background: '#f9fafb', borderRadius: '8px', padding: '10px', wordBreak: 'break-all' }}>
                          {shareEmailModal.invitation?.invitationUrl}
                        </div>
                        {shareEmailModal.invitation?.expiresAt && (
                          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
                            {`Scadenza link di attivazione: ${new Date(shareEmailModal.invitation.expiresAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {(shareEmailModal.success || shareEmailModal.error) && (
                <div style={{ marginTop: '16px', marginBottom: '10px' }}>
                  {shareEmailModal.success && (
                    <div style={{ background: '#f0fff4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#166534' }}>
                      ✅ {shareEmailModal.success}
                    </div>
                  )}
                  {shareEmailModal.error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#b91c1c' }}>
                      {shareEmailModal.error}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
                <button
                  type="button"
                  onClick={closeShareEmailModal}
                  style={{ padding: '8px 18px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', background: 'white' }}
                >
                  Chiudi
                </button>
                <button
                  type="submit"
                  disabled={shareEmailModal.sending}
                  style={{ padding: '8px 18px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  {shareEmailModal.sending ? 'Invio...' : 'Invia email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Link Modal */}
      {viewLinkModal.open && viewLinkModal.inv && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '480px', maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 16px' }}>Link di Invito</h3>
            <div style={{ background: '#f0fff4', border: '1px solid #86efac', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '13px' }}>
                {viewLinkModal.inv.label || 'Link senza nome'}
              </p>
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#555' }}>
                Usi: {viewLinkModal.inv.uses}{viewLinkModal.inv.maxUses === -1 ? ' (illimitati)' : `/${viewLinkModal.inv.maxUses}`}
                {viewLinkModal.inv.expiresAt ? ` · Scade: ${new Date(viewLinkModal.inv.expiresAt).toLocaleDateString('it-IT')}` : ' · Nessuna scadenza'}
              </p>
              <input
                readOnly
                value={viewLinkModal.inv.invitationUrl}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }}
                onClick={e => e.target.select()}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                <button
                  onClick={() => navigator.clipboard.writeText(viewLinkModal.inv.invitationUrl)}
                  style={{ padding: '6px 12px', background: '#C21F7A', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                >
                  📋 Copia link
                </button>
                <button
                  onClick={() => openShareEmailModal(selectedOffer?.id, viewLinkModal.inv, selectedOffer?.label || '', selectedOffer?.expiresAt || null)}
                  style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                >
                  📧 Invia via email
                </button>
              </div>
            </div>
            <button
              onClick={() => setViewLinkModal({ open: false, inv: null })}
              style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Edit Offer Modal */}
      {editModal.open && editModal.offer && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '760px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px' }}>Modifica Offerta</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>{editModal.offer.label}</p>
            {editError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#dc2626' }}>
                {editError}
              </div>
            )}
            {editWarning && (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
                ⚠️ <strong>Offerta salvata.</strong> {editWarning.voidedContractOffers} contratto/i in attesa di firma {editWarning.voidedContractOffers === 1 ? 'è stato annullato' : 'sono stati annullati'} perché le condizioni economiche sono cambiate: il tenant riceverà automaticamente un nuovo documento aggiornato al prossimo accesso.
                <div style={{ marginTop: '8px' }}>
                  <button type="button" onClick={() => setEditModal({ open: false, offer: null })} style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Ho capito, chiudi</button>
                </div>
              </div>
            )}
            <form onSubmit={handleEdit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: '12px', alignItems: 'start' }}>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', background: '#ffffff' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Label *</label>
                    <input
                      required
                      value={editForm.label}
                      onChange={e => setEditForm(p => ({ ...p, label: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
                    />
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>💡 Questa label è visibile al cliente nella lista dei suoi tenant (dashboard manager).</p>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>
                      Scadenza offerta
                      <span style={{ marginLeft: '6px', fontWeight: 400, color: '#666', fontSize: '12px' }}>(lascia vuoto = nessuna scadenza)</span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="datetime-local"
                        value={editForm.expiresAt}
                        onChange={e => setEditForm(p => ({ ...p, expiresAt: e.target.value }))}
                        style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                      {editForm.expiresAt && (
                        <button
                          type="button"
                          onClick={() => setEditForm(p => ({ ...p, expiresAt: '' }))}
                          style={{ padding: '8px 12px', border: '1px solid #ef4444', borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#ef4444', fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          Rimuovi scadenza
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Condizioni particolari e note</label>
                    <textarea
                      value={editForm.notes}
                      onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                      rows={3}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
                    />
                  </div>

                </div>

                <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', background: '#ffffff' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#7c3aed', marginBottom: '10px' }}>
                    Gestione override funzionali
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, fontSize: '13px', color: '#1f2937' }}>
                      Piano base *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        required
                        value={editForm.basePlanVersionId}
                        onChange={e => handleEditBasePlanChange(e.target.value)}
                        disabled={editModal.offer._count?.contractOffers > 0}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 38px 10px 12px', border: '1px solid #c4b5fd', borderRadius: '10px', fontSize: '14px', fontWeight: 600, color: '#5b21b6', background: editModal.offer._count?.contractOffers > 0 ? '#f5f3ff' : 'linear-gradient(180deg, #ffffff 0%, #faf7ff 100%)', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        {planVersionOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#7c3aed', pointerEvents: 'none', fontSize: '12px' }}>▾</span>
                    </div>
                    {(() => {
                      const selectedPlanVersion = plansCatalog.flatMap((plan) => plan.versions || []).find((version) => version.id === editForm.basePlanVersionId);
                      return renderPlanPriceSummary(selectedPlanVersion);
                    })()}
                  </div>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#6b7280' }}>
                    Cambiando piano base, la lista viene ricaricata con i valori del nuovo piano.
                  </p>
                  {renderPriceOverrideCard(editForm, setEditForm, editModal.offer._count?.contractOffers > 0)}
                  {renderFeeOverrideCard(
                    editForm.feeOverridePercent, editForm.feeOverrideFixedCents,
                    (field, val) => setEditForm(p => ({ ...p, [field]: val })),
                    editForm.basePlanVersionId,
                    editModal.offer._count?.contractOffers > 0
                  )}
                  {renderTrialOverrideCard(
                    editForm.trialDaysOverride,
                    (field, val) => setEditForm(p => ({ ...p, [field]: val })),
                    editForm.basePlanVersionId,
                    editModal.offer._count?.contractOffers > 0
                  )}
                  {renderPromotionCodeSelector(
                    editForm.automaticPromotionCodeId,
                    (field, val) => {
                      setEditCouponNotice('');
                      setEditForm(p => ({ ...p, [field]: val }));
                    },
                    editForm.basePlanVersionId,
                    editModal.offer._count?.contractOffers > 0,
                    editForm
                  )}
                  {editCouponNotice && (
                    <div style={{ margin: '-4px 0 14px', padding: '9px 11px', borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600 }}>
                      {editCouponNotice}
                    </div>
                  )}
                  {renderOverrideEditor(editOverrideRows, setEditOverrideRows, editModal.offer._count?.contractOffers > 0)}
                </div>
              </div>
              <div style={{ marginTop: '20px', display: 'flex', gap: '8px' }}>
                <button
                  type="submit"
                  disabled={editSubmitting || !getPromotionCodeCompatibility(editForm.automaticPromotionCodeId, editForm.basePlanVersionId, editForm).compatible}
                  style={{ padding: '10px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  {editSubmitting ? 'Salvataggio...' : 'Salva Modifiche'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditModal({ open: false, offer: null })}
                  style={{ padding: '10px 24px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete / Deactivate Modal */}
      {deleteModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '460px', maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '17px' }}>Elimina offerta</h3>

            {deleteModal.loading ? (
              <p style={{ color: '#666', fontSize: '14px' }}>Caricamento dipendenze...</p>
            ) : deleteModal.checkData ? (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#444' }}>
                  Offerta: <strong>{deleteModal.checkData.label}</strong>
                </p>

                {/* Dependency summary */}
                <div style={{ background: '#f8f8f8', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '14px', marginBottom: '20px', fontSize: '13px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 16px', lineHeight: '1.6' }}>
                    <span>Sessioni onboarding attive</span>
                    <span style={{ fontWeight: 700, color: deleteModal.checkData.activeSessions > 0 ? '#dc2626' : '#16a34a', textAlign: 'right' }}>
                      {deleteModal.checkData.activeSessions}
                    </span>
                    <span>Contratti in firma (AWAITING)</span>
                    <span style={{ fontWeight: 700, color: deleteModal.checkData.contractOffers.awaitingSignature > 0 ? '#dc2626' : '#16a34a', textAlign: 'right' }}>
                      {deleteModal.checkData.contractOffers.awaitingSignature}
                    </span>
                    <span>Contratti firmati (SIGNED)</span>
                    <span style={{ fontWeight: 700, color: deleteModal.checkData.contractOffers.signed > 0 ? '#b45309' : '#16a34a', textAlign: 'right' }}>
                      {deleteModal.checkData.contractOffers.signed}
                    </span>
                    <span>Contratti in vigore (EFFECTIVE)</span>
                    <span style={{ fontWeight: 700, color: deleteModal.checkData.contractOffers.effective > 0 ? '#b45309' : '#16a34a', textAlign: 'right' }}>
                      {deleteModal.checkData.contractOffers.effective ?? 0}
                    </span>
                    <span>Contratti annullati (VOIDED)</span>
                    <span style={{ fontWeight: 700, color: '#6b7280', textAlign: 'right' }}>
                      {deleteModal.checkData.contractOffers.voided}
                    </span>
                  </div>
                </div>

                {/* Warnings */}
                {deleteModal.checkData.activeSessions > 0 && deleteModal.checkData.canSoftDelete && (
                  <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#92400e' }}>
                    ⚠️ <strong>{deleteModal.checkData.activeSessions} onboarding in corso</strong> hanno questa offerta bloccata. Disattivando o cancellando questa offerta, quei tenant non potranno più procedere al passo contratto: vedranno un messaggio di errore con la possibilità di cancellare la richiesta e registrare un nuovo contratto.
                  </div>
                )}
                {!deleteModal.checkData.canHardDelete && (
                  <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
                    ⚠️ Eliminazione definitiva non disponibile: ci sono contratti firmati collegati a questa offerta.
                  </div>
                )}


                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <button
                    onClick={() => setDeleteModal({ open: false, offerId: null, checkData: null, loading: false, acting: false })}
                    disabled={deleteModal.acting}
                    style={{ padding: '8px 18px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', background: 'white', fontSize: '13px' }}
                  >
                    Annulla
                  </button>
                  {deleteModal.checkData.canSoftDelete && (
                    <button
                      onClick={handleSoftDelete}
                      disabled={deleteModal.acting}
                      title="Disattiva: nascondi dai nuovi link ma mantieni lo storico"
                      style={{ padding: '8px 18px', border: '1px solid #f59e0b', borderRadius: '6px', cursor: 'pointer', background: '#fffbeb', color: '#92400e', fontSize: '13px', fontWeight: 600 }}
                    >
                      {deleteModal.acting ? '...' : '⚠️ Disattiva'}
                    </button>
                  )}
                  {deleteModal.checkData.canHardDelete && (
                    <button
                      onClick={handleHardDelete}
                      disabled={deleteModal.acting}
                      title="Elimina definitivamente dal database"
                      style={{ padding: '8px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: '#dc2626', color: 'white', fontSize: '13px', fontWeight: 600 }}
                    >
                      {deleteModal.acting ? '...' : '🗑 Elimina definitivamente'}
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Signed Contracts Modal */}
      {signedContractsModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Contratti Firmati</h2>
              <button
                onClick={() => setSignedContractsModal({ open: false, offerId: null, contracts: [], loading: false, error: '' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', padding: 0, lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {signedContractsModal.loading && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                <p>Caricamento...</p>
              </div>
            )}

            {signedContractsModal.error && (
              <div style={{ background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '6px', padding: '12px', marginBottom: '16px', color: '#991b1b', fontSize: '14px' }}>
                ❌ {signedContractsModal.error}
              </div>
            )}

            {!signedContractsModal.loading && !signedContractsModal.error && signedContractsModal.contracts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                <p>Nessun contratto firmato</p>
              </div>
            )}

            {!signedContractsModal.loading && signedContractsModal.contracts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {signedContractsModal.contracts.map((contract, idx) => (
                  <div
                    key={contract.id || idx}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '12px',
                      background: '#f9fafb'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 600 }}>
                        {contract.tenant?.company_name || contract.tenantId || 'Sconosciuto'}
                      </div>
                      {contract.tenant?.status && (
                        <span style={{
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginLeft: '8px',
                          ...(() => {
                            const badgeConfig = getStatusBadge(contract.tenant.status);
                            return {
                              backgroundColor: badgeConfig.color,
                              color: 'white'
                            };
                          })()
                        }}>
                          {getStatusBadge(contract.tenant.status).label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      📧 {contract.tenant?.email || '—'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      📅 Firmato il {new Date(contract.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setSignedContractsModal({ open: false, offerId: null, contracts: [], loading: false, error: '' })}
                style={{ padding: '8px 18px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', background: 'white', fontSize: '13px' }}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialOffers;
